import axios from 'axios';
import toast from '../utils/toast';
import { formatDateForInput, getTodayInSingaporeTimezone } from '../utils/dateUtils';

// Compute API base URL synchronously without network probes
const envApiUrl = process.env.REACT_APP_API_URL;
let API_BASE_URL = 'http://localhost:6675/api/v1';
let configSource = 'default';

if (envApiUrl) {
  API_BASE_URL = envApiUrl;
  configSource = 'REACT_APP_API_URL';
} else if (process.env.NODE_ENV !== 'production') {
  // In development without explicit API_URL, use same-origin path to leverage CRA proxy
  API_BASE_URL = '/api/v1';
  configSource = 'development default';
} else {
  // Production fallback (matches start_local_development.bat port 6675)
  API_BASE_URL = 'http://localhost:6675/api/v1';
  configSource = 'production fallback';
}

if (process.env.NODE_ENV !== 'production') {
  console.log(`📡 API Configuration: source=${configSource} url=${API_BASE_URL}`);
}

// Create base axios instance
const api = axios.create({
  baseURL: API_BASE_URL,
  timeout: 30000, // 30 s hard ceiling — prevents hung requests from accumulating
  headers: {
    'Content-Type': 'application/json',
    'Accept': 'application/json',
  },
});

// Create a separate instance for auth
const authApi = axios.create({
  baseURL: API_BASE_URL,
  timeout: 15000, // Auth endpoints should be fast
  headers: {
    'Content-Type': 'application/json',
    'Accept': 'application/json',
  },
});

// Defensive HTTPS upgrade for absolute same-host URLs.
//
// Why: the backend builds some absolute URLs via DRF's `reverse(..., request=request)`
// (e.g. `EventPhotoSerializer.image_url`, `TransferPhotoSerializer.photo_url`).
// Django decides the scheme from `request.is_secure()`, which depends on the
// reverse proxy forwarding `X-Forwarded-Proto: https`. When that header is
// missing in production, the serializer returns `http://hr.wepl.site/...`,
// and the browser blocks it as Mixed Content because the SPA itself is on
// HTTPS.
//
// Mirrors the browser's `upgrade-insecure-requests` CSP behavior, but only
// for same-host targets so we never silently change a third-party HTTP URL.
// The proper long-term fix is at the proxy (`proxy_set_header X-Forwarded-Proto $scheme;`),
// but this keeps photo loading working even if that header gets dropped.
const upgradeSameHostScheme = (url) => {
  if (typeof url !== 'string' || !url.startsWith('http://')) return url;
  if (typeof window === 'undefined' || window.location?.protocol !== 'https:') return url;
  try {
    const target = new URL(url);
    if (target.host === window.location.host) {
      target.protocol = 'https:';
      return target.toString();
    }
  } catch (_) {
    // Malformed URL — leave it alone and let axios surface the failure.
  }
  return url;
};

// Add auth token to requests
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('accessToken') || sessionStorage.getItem('accessToken');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }

  if (config.url) {
    config.url = upgradeSameHostScheme(config.url);
  }
  if (config.baseURL) {
    config.baseURL = upgradeSameHostScheme(config.baseURL);
  }

  // CRITICAL: Don't set Content-Type for FormData - browser will set it with boundary
  // Only set Content-Type for non-FormData requests
  if (!(config.data instanceof FormData) && !config.headers['Content-Type']) {
    config.headers['Content-Type'] = 'application/json';
  } else if (config.data instanceof FormData) {
    // Remove Content-Type header if it was set - browser will add it with boundary
    delete config.headers['Content-Type'];
  }
  return config;
});

// Same scheme-upgrade guard for the auth instance — refresh/login calls also
// hit the backend, so they need the same protection if the proxy ever drops
// X-Forwarded-Proto.
authApi.interceptors.request.use((config) => {
  if (config.url) {
    config.url = upgradeSameHostScheme(config.url);
  }
  if (config.baseURL) {
    config.baseURL = upgradeSameHostScheme(config.baseURL);
  }
  return config;
});

// Single-flight token refresh.
//
// Why: notification polling fires `/notifications/` and `/notifications/unread_count/`
// together every 60s. When the access token has expired, BOTH 401 at the same
// instant and would each POST `/token/refresh/`. With ROTATE_REFRESH_TOKENS +
// BLACKLIST_AFTER_ROTATION enabled, the first refresh rotates+blacklists the old
// refresh token; the second concurrent refresh then sends that blacklisted token,
// 401s, and force-logs-out the user. We serialize: the first 401 performs the
// refresh, every other concurrent 401 subscribes and retries with the new token.
let isRefreshing = false;
let refreshSubscribers = [];

const subscribeTokenRefresh = (cb) => {
  refreshSubscribers.push(cb);
};

const flushSubscribers = (newAccessToken) => {
  const subscribers = refreshSubscribers;
  refreshSubscribers = [];
  subscribers.forEach((cb) => cb(newAccessToken));
};

// Performs the actual refresh and persists the rotated tokens. Returns the new
// access token, or null when there is no refresh token to use.
const performTokenRefresh = async () => {
  const refreshToken = localStorage.getItem('refreshToken') || sessionStorage.getItem('refreshToken');
  if (!refreshToken) return null;

  const response = await authApi.post('/token/refresh/', {
    refresh: refreshToken,
  });
  const { access, refresh: newRefreshToken } = response.data;

  // Always persist to localStorage (iOS/Android PWA compatible).
  // Migrate legacy sessionStorage sessions on first successful refresh.
  const wasInSessionStorage = !localStorage.getItem('refreshToken') && !!sessionStorage.getItem('refreshToken');
  if (wasInSessionStorage) {
    sessionStorage.removeItem('accessToken');
    sessionStorage.removeItem('refreshToken');
    // Treat migrated session as non-remembered with a 12-hour window
    localStorage.setItem('sessionExpiry', String(Date.now() + 12 * 60 * 60 * 1000));
  }

  localStorage.setItem('accessToken', access);

  // Save the rotated refresh token — critical when ROTATE_REFRESH_TOKENS=True
  if (newRefreshToken) {
    localStorage.setItem('refreshToken', newRefreshToken);
    // Slide the expiry window forward for non-remembered sessions
    if (localStorage.getItem('sessionExpiry')) {
      localStorage.setItem('sessionExpiry', String(Date.now() + 12 * 60 * 60 * 1000));
    }
  }

  return access;
};

// Apply a freshly minted access token to a queued request and retry it.
const retryWithToken = (originalRequest, newAccessToken) => {
  originalRequest.headers = originalRequest.headers || {};
  originalRequest.headers.Authorization = `Bearer ${newAccessToken}`;
  // Preserve FormData: browser sets Content-Type with boundary automatically.
  if (originalRequest.data instanceof FormData) {
    delete originalRequest.headers['Content-Type'];
  }
  return api(originalRequest);
};

// Handle token refresh
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    // Suppress logging for intentionally aborted/cancelled requests
    const isCancelled = error.name === 'CanceledError' || error.name === 'AbortError' || error.code === 'ERR_CANCELED';
    // Some endpoints use a 4xx as a *control-flow* signal rather than a
    // failure (e.g. Talent Insights' optimistic individual-result call
    // returns 400 `justification_required` to mean "no grace window —
    // open the gate"). Call sites opt-in by passing
    // `{ expectedStatuses: [400, ...] }` in the axios config; the response
    // is still thrown so the caller's `catch` runs, but we don't pollute
    // the console with a red ❌ that looks like a bug.
    const expectedStatuses = error.config?.expectedStatuses;
    const isExpectedStatus = Array.isArray(expectedStatuses)
      && error.response
      && expectedStatuses.includes(error.response.status);
    if (!isCancelled && !isExpectedStatus) {
      console.error(`❌ API Error: ${error.config?.method?.toUpperCase()} ${error.config?.url}`, {
        status: error.response?.status,
        statusText: error.response?.statusText,
        data: error.response?.data
      });
    }
    
    const originalRequest = error.config;
    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;
      const refreshToken = localStorage.getItem('refreshToken') || sessionStorage.getItem('refreshToken');
      if (!refreshToken) {
        return Promise.reject(error);
      }

      // A refresh is already in flight: queue this request and retry once the
      // single shared refresh resolves. A null token means the refresh failed.
      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          subscribeTokenRefresh((newAccessToken) => {
            if (!newAccessToken) {
              reject(error);
              return;
            }
            resolve(retryWithToken(originalRequest, newAccessToken));
          });
        });
      }

      isRefreshing = true;
      try {
        const access = await performTokenRefresh();
        isRefreshing = false;
        // Release every queued request with the new token.
        flushSubscribers(access);
        return retryWithToken(originalRequest, access);
      } catch (refreshError) {
        isRefreshing = false;
        // Wake queued requests so they reject instead of hanging forever.
        flushSubscribers(null);
        localStorage.clear();
        sessionStorage.clear();
        window.location.href = '/login';
        return Promise.reject(refreshError);
      }
    }
    return Promise.reject(error);
  }
);

// Helper functions for data transformation
const toStringOrUndefined = (v) => (v === undefined || v === null || v === '' ? undefined : String(v));
const toNumberOrUndefined = (v) => {
  if (v === undefined || v === null || v === '') return undefined;
  const n = Number(v);
  return Number.isNaN(n) ? undefined : n;
};

// Helper for date fields - ensure proper format for backend DateField
const toDateOrUndefined = (v) => {
  if (v === undefined || v === null || v === '') return undefined;
  // If already a date string, return as is
  if (typeof v === 'string' && v.match(/^\d{4}-\d{2}-\d{2}$/)) return v;
  // Try to format as YYYY-MM-DD
  try {
    const date = new Date(v);
    if (!isNaN(date.getTime())) {
      return formatDateForInput(date);
    }
  } catch (e) {
    console.warn('Invalid date format:', v);
  }
  return undefined;
};

// Helper: sanitize payload for LeaveApplication endpoints to match backend schema
export const sanitizeLeaveApplicationData = (inputData = {}) => {
  const normalizePkFlexible = (v) => {
    if (v === undefined || v === null || v === '') return undefined;
    if (typeof v === 'string') {
      const trimmed = v.trim();
      // If looks like UUID, keep as string
      const uuidLike = /^[0-9a-fA-F-]{32,36}$/.test(trimmed);
      if (uuidLike) return trimmed;
      // If numeric string, convert to number
      const n = Number(trimmed);
      return Number.isNaN(n) ? trimmed : n;
    }
    if (typeof v === 'number') return v;
    // Fallback: keep as-is
    return v;
  };

  const payload = {
    leave_type_id: normalizePkFlexible(inputData.leave_type_id ?? inputData.leave_type?.id),
    leave_start_date: inputData.leave_start_date || undefined,
    leave_end_date: inputData.leave_end_date || inputData.leave_start_date || undefined,
    leave_reason: inputData.leave_reason ?? '',
    leave_emergency: !!inputData.leave_emergency,
    leave_coverage_staff_id: normalizePkFlexible(inputData.leave_coverage_staff_id),
    leave_status: inputData.leave_status || 'submitted',
    // Time period fields for unified leave calculation
    // For sick leave types, default to full-day (AM start, PM end)
    // For other types, use provided values or defaults
    leave_start_time_period: inputData.leave_start_time_period || 'AM',
    leave_end_time_period: inputData.leave_end_time_period || 'PM',
    // Off in Lieu specific fields
    saturday_work_date: inputData.saturday_work_date || undefined,
    work_description: inputData.work_description || undefined,
    project_code: inputData.project_code || undefined,
    oil_dates: Array.isArray(inputData.oil_dates) && inputData.oil_dates.length > 0
      ? inputData.oil_dates
          .filter(e => e && (e.date || e.work_date) && e.days)
          .map(e => ({ work_date: e.date || e.work_date, days: Number(e.days) }))
      : undefined,
    // NEW: External Activity specific fields
    activity_type: inputData.activity_type || undefined,
    activity_location: inputData.activity_location || undefined,
    activity_organizer: inputData.activity_organizer || undefined,
    expected_duration: inputData.expected_duration || undefined,
    business_justification: inputData.business_justification || undefined,
    // NEW: WFH specific fields
    wfh_reason: inputData.wfh_reason ?? undefined,
    wfh_other_reason: inputData.wfh_other_reason ?? undefined,
    // Compassionate Leave specific fields
    compassionate_relationship: toStringOrUndefined(inputData.compassionate_relationship),
    deceased_name: toStringOrUndefined(
      typeof inputData.deceased_name === 'string'
        ? inputData.deceased_name.trim()
        : inputData.deceased_name
    ),
    // Staff overseas-travel declaration (levy-liable WP / S Pass). Omitted
    // unless the caller supplied them so worker ticket payloads stay untouched.
    // NEW: NS Leave specific fields
    serial_number: inputData.serial_number || undefined,
    issue_date: inputData.issue_date || undefined,
    document_approval_id: normalizePkFlexible(inputData.document_approval_id),
  };

  const hasOverseasDeclaration =
    Object.prototype.hasOwnProperty.call(inputData, 'overseas_travel_declared')
    || Object.prototype.hasOwnProperty.call(inputData, 'overseas_departure_date')
    || Object.prototype.hasOwnProperty.call(inputData, 'overseas_return_date');
  if (hasOverseasDeclaration) {
    payload.overseas_travel_declared =
      inputData.overseas_travel_declared === true
        ? true
        : inputData.overseas_travel_declared === false
          ? false
          : null;
    payload.overseas_departure_date = inputData.overseas_departure_date || null;
    payload.overseas_return_date = inputData.overseas_return_date || null;
  }

  // Handle OCR data fields if present
  if (inputData.ocr_data) {
    const ocrData = inputData.ocr_data;
    payload.ocr_patient_nric_fin = toStringOrUndefined(ocrData.ocr_patient_nric_fin);
    payload.ocr_patient_full_name = toStringOrUndefined(ocrData.ocr_patient_full_name);
    payload.ocr_mc_number = toStringOrUndefined(ocrData.ocr_mc_number);
    payload.ocr_mc_issue_date = toDateOrUndefined(ocrData.ocr_mc_issue_date);
    payload.ocr_mc_start_date = toDateOrUndefined(ocrData.ocr_mc_start_date);
    payload.ocr_mc_end_date = toDateOrUndefined(ocrData.ocr_mc_end_date);
    payload.ocr_mc_total_days = toNumberOrUndefined(ocrData.ocr_mc_total_days);
    payload.ocr_doctor_name = toStringOrUndefined(ocrData.ocr_doctor_name);
    payload.ocr_clinic_hospital_name = toStringOrUndefined(ocrData.ocr_clinic_hospital_name);
    payload.ocr_diagnosis = toStringOrUndefined(ocrData.ocr_diagnosis);
  }

  // Remove keys with undefined to avoid sending unknown/empty fields
  return Object.fromEntries(Object.entries(payload).filter(([, v]) => v !== undefined));
};

// Auth API
export const authAPI = {
  // Send both identifier (new) and work_email (legacy) for compatibility with older backends
  // remember_me signals the backend to issue a 30-day refresh token instead of 12-hour
  login: (identifier, password, rememberMe = false) => authApi.post('/token/', {
    identifier, work_email: identifier, password, remember_me: rememberMe,
  }),
  refreshToken: (refreshToken) => authApi.post('/token/refresh/', { refresh: refreshToken }),
  verifyToken: (token) => authApi.post('/token/verify/', { token }),
  
  // Password management - for authenticated users
  changePassword: (currentPassword, newPassword) => api.post('/staff/change_password/', { 
    current_password: currentPassword, 
    new_password: newPassword 
  }),
  
  // Password reset - for unauthenticated users (forgot password flow)
  // Accepts either email string or object with email/staff_id
  forgotPassword: (identifier) => {
    const payload = typeof identifier === 'string' 
      ? { email: identifier }
      : identifier;
    return authApi.post('/auth/forgot-password/', payload);
  },
  // Account setup - for first-time account setup (welcome email flow)
  // Accepts either email string or object with email/staff_id
  requestSetupAccount: (identifier) => {
    const payload = typeof identifier === 'string' 
      ? { email: identifier }
      : identifier;
    return authApi.post('/auth/request-setup-account/', payload);
  },
  resetPassword: (uid, token, newPassword) => authApi.post('/auth/reset-password/', { 
    uid, 
    token, 
    new_password: newPassword 
  }),
  validateResetToken: (uid, token) => authApi.post('/auth/validate-reset-token/', { uid, token }),

  // Launch-code SSO (opens an external WEPL app with the current session).
  // `app` selects the target ('petty_cash' | 'transport'); defaults server-side.
  getLaunchCode: (app) => api.post('/auth/launch-code/', app ? { app } : {}),
};

// Staff API
export const staffAPI = {
  // User management
  getCurrentUser: () => api.get('/staff/me/'),
  getMyProfile: () => api.get('/staff/me/'),
  /** Medical claim eligibility (Clause 8) for petty-cash reimbursements. */
  getClaimEligibility: (params = {}) =>
    api.get('/staff/me/claim-eligibility/', { params }),
  /** Transport-owned portal access flags for navigation and launch controls. */
  getTransportPortalAccess: () =>
    api.get('/staff/me/transport-portal-access/'),
  updateMyProfile: (data) => api.patch('/staff/me/', data),
  getStaffList: (params) => api.get('/staff/', { params }),
  getStaff: (staffId) => api.get(`/staff/${staffId}/`),
  exportStaff: (params) => api.get('/staff/export/', { params, responseType: 'blob' }),
  getExportFields: () => api.get('/staff/export_fields/'),
  updateProfile: (staffId, data) => api.patch(`/staff/${staffId}/`, data),
  updateStaff: (staffId, data) => api.patch(`/staff/${staffId}/`, data),

  // Employment Movements (designation / department / trade history)
  getEmploymentMovements: (staffId) => api.get(`/staff/${staffId}/employment-movements/`),
  recordEmploymentChange: (staffId, data) => api.post(`/staff/${staffId}/record-employment-change/`, data),
  amendProbation: (staffId, data) => api.post(`/staff/${staffId}/amend-probation/`, data),
  cancelScheduledEmploymentChange: (staffId, movementId) =>
    api.post(`/staff/${staffId}/employment-movements/${movementId}/cancel/`),
  // Profile change log — direct-edit (correction) audit trail for a StaffUser.
  getStaffAuditLog: (staffId, extraParams = {}) => api.get('/admin/staff_core/auditlog/', {
    params: {
      audit_model_name: 'StaffUser',
      audit_staff_id__staff_id: staffId,
      ordering: '-audit_timestamp',
      page_size: 50,
      ...extraParams,
    },
  }),
  getDistinctDesignations: (params = {}) => api.get('/staff/distinct-designations/', { params }),
  // Preview the next staff_id for a classification (staff|worker). Non-locking
  // preview; the authoritative ID is allocated atomically at create time.
  getNextStaffId: (classification) =>
    api.get('/staff/next-staff-id/', { params: { classification } }),

  // Asset Custodian Management (HR Admin only)
  getCustodians: () => api.get('/staff/custodians/'),
  updateCustodianCategories: (staffId, categoryIds) =>
    api.patch(`/staff/${staffId}/custodian-categories/`, { category_ids: categoryIds }),
  
  // Statistics endpoints - restricted to HR/admin users
  getDashboardStats: async () => {
    try {
      return await api.get('/staff/dashboard_stats/');
    } catch (error) {
      const status = error.response?.status;
      // Handle 403 Forbidden (insufficient permissions) silently
      if (status === 403) {
        console.log('Dashboard stats access denied - restricted to HR/admin users');
        // Return zero values instead of throwing error
        return {
          data: {
            annual_leave: { value: '0 days', change: '0', change_type: 'neutral' },
            sick_leave: { value: '0 days', change: '0', change_type: 'neutral' },
            attendance: { value: '0%', change: '0%', change_type: 'neutral' },
            hours_this_week: { value: '0h', change: '0h', change_type: 'neutral' }
          }
        };
      }

      // For other errors, try fallback metrics
      if ([500, 401, 404].includes(status)) {
        try {
          console.log('Trying dashboard_metrics fallback...');
          const res = await api.get('/staff/dashboard_metrics/');
          const m = res.data || {};
          console.log('Dashboard metrics fallback successful:', m);
          // Map metrics to dashboard_stats shape as a fallback
          return {
            data: {
              annual_leave: { value: '0 days', change: '0', change_type: 'neutral' },
              sick_leave: { value: '0 days', change: '0', change_type: 'neutral' },
              attendance: {
                value: (m.attendance_percentage !== undefined && m.attendance_percentage !== null) ? `${m.attendance_percentage}%` : '0%',
                change: (m.attendance_percentage !== undefined && m.attendance_percentage !== null) ? `${m.attendance_percentage}%` : '0%',
                change_type: 'neutral'
              },
              hours_this_week: { value: '0h', change: '0h', change_type: 'neutral' }
            }
          };
        } catch (fallbackErr) {
          console.log('Dashboard metrics fallback also failed, using zeros:', fallbackErr.response?.status);
          // Final fallback to zeros
          return {
            data: {
              annual_leave: { value: '0 days', change: '0', change_type: 'neutral' },
              sick_leave: { value: '0 days', change: '0', change_type: 'neutral' },
              attendance: { value: '0%', change: '0%', change_type: 'neutral' },
              hours_this_week: { value: '0h', change: '0h', change_type: 'neutral' }
            }
          };
        }
      }
      throw error;
    }
  },
  getDashboardMetrics: () => api.get('/staff/dashboard_metrics/'),
  // Personal admin navigation ranking (daily-stable, based on 30-day usage)
  getAdminNavigationRanking: () => api.get('/staff/admin-navigation-ranking/'),
  recordAdminNavigationVisit: (itemKey) =>
    api.post('/staff/admin-navigation-visits/', { item_key: itemKey }),
  // Platform usage / adoption analytics (rolling window, days param)
  getUsageAnalytics: (params = {}) => api.get('/staff/usage_analytics/', { params }),
  // Employee-behaviour rhythm (weekday×hour by interaction type + segment)
  getUsageBehavior: (params = {}) => api.get('/staff/usage_behavior/', { params }),
  // Pending Document Approvals (HR)
  getPendingDocumentApprovalsSummary: () => api.get('/document-approvals/pending-summary/'),
  getStaffStatistics: () => api.get('/staff/statistics/'),
  getFilteredStaffStatistics: (params) => api.get('/staff/statistics/', { params }),
  getAgeGroupStats: () => api.get('/staff/age_group_stats/'),
  getClassificationAgeStats: () => api.get('/staff/classification_age_stats/'),
  getNationalityStats: () => api.get('/staff/nationality_stats/'),
  getTenureDistributionStats: () => api.get('/staff/tenure_distribution_stats/'),
  getGenderDistributionStats: () => api.get('/staff/gender_distribution_stats/'),
  getRaceDistributionStats: () => api.get('/staff/race_distribution_stats/'),
  getDepartmentAnalyticsStats: () => api.get('/staff/department_analytics_stats/'),
  getEmploymentPassBreakdownStats: () => api.get('/staff/employment_pass_breakdown_stats/'),
  getWorkforceAgeProfile: () => api.get('/staff/workforce_age_profile/'),
  getRetirementRisk: () => api.get('/staff/retirement_risk/'),
  getNewHireRetention: () => api.get('/staff/new_hire_retention/'),
  getHeadcountMovements: () => api.get('/staff/headcount_movements/'),
  getStaffWorkerRatio: () => api.get('/staff/staff_worker_ratio/'),
  getWorkforceMetricsTrends: () => api.get('/staff/workforce_metrics_trends/'),
  
  // Departments
  getDepartments: () => api.get('/departments/'),
  
  // Leave Management
  getLeaveTypes: () => api.get('/configuration/leave-types/'),
  getAvailableLeaveTypes: (params = {}) => api.get('/leave-eligibility/', { params }),  // Accept params e.g., { staff_id, strict }
  createLeaveType: (data) => api.post('/configuration/leave-types/', data),
  updateLeaveType: (id, data) => api.patch(`/configuration/leave-types/${id}/`, data),
  deleteLeaveType: (id) => api.delete(`/configuration/leave-types/${id}/`),
  bulkUpdateLeaveTypes: (data) => api.post('/configuration/leave-types/bulk_update/', data),
  updateMOMStatutoryRates: () => api.post('/configuration/leave-types/update_mom_rates/'),
  
  getLeaveApplications: (params, config = {}) => api.get('/leave-applications/', { params, ...config }),
  exportLeaveApplications: (params) => api.get('/leave-applications/export/', { params, responseType: 'blob' }),
  getMyLeaveApplications: (params) => api.get('/leave-applications/my_applications/', { params }),
  getLeaveApplication: (id) => api.get(`/leave-applications/${id}/`),
  getLeaveApplicationAdminDetail: (id) => api.get(`/leave-applications/${id}/admin_detail/`),
  getDepartmentCalendar: (params) => api.get('/leave-applications/department_calendar/', { params }),
  getCompanyCalendar: (params) => api.get('/leave-applications/company_calendar/', { params }),
  
  // Configuration Management
  getDepartments: () => api.get('/configuration/departments/'),
  createDepartment: (data) => api.post('/configuration/departments/', data),
  updateDepartment: (id, data) => api.patch(`/configuration/departments/${id}/`, data),
  deleteDepartment: (id) => api.delete(`/configuration/departments/${id}/`),
  
  getLocations: () => api.get('/configuration/locations/'),
  createLocation: (data) => api.post('/configuration/locations/', data),
  updateLocation: (id, data) => api.patch(`/configuration/locations/${id}/`, data),
  deleteLocation: (id) => api.delete(`/configuration/locations/${id}/`),
  
  getClassifications: () => api.get('/configuration/classifications/'),
  createClassification: (data) => api.post('/configuration/classifications/', data),
  updateClassification: (id, data) => api.patch(`/configuration/classifications/${id}/`, data),
  deleteClassification: (id) => api.delete(`/configuration/classifications/${id}/`),
  
  getDesignations: () => api.get('/configuration/designations/'),
  createDesignation: (data) => api.post('/configuration/designations/', data),
  updateDesignation: (id, data) => api.patch(`/configuration/designations/${id}/`, data),
  deleteDesignation: (id) => api.delete(`/configuration/designations/${id}/`),
  
  // Certificates
  getCertificates: (params = {}) => api.get('/certificates/', { params }),
  createCertificate: (data) => api.post('/certificates/', data),
  updateCertificate: (id, data) => api.patch(`/certificates/${id}/`, data),
  deleteCertificate: (id) => api.delete(`/certificates/${id}/`),
  createLeaveApplication: (data) => {
    const sanitizedData = sanitizeLeaveApplicationData(data);
    console.log('🔍 Sanitized data being sent to backend:', sanitizedData);
    console.log('🔍 WFH reason fields:', { wfh_reason: sanitizedData.wfh_reason, wfh_other_reason: sanitizedData.wfh_other_reason });
    return api.post('/leave-applications/', sanitizedData);
  },
  createLeaveApplicationDraft: (data) => api.post('/leave-applications/', sanitizeLeaveApplicationData({ ...data, leave_status: 'draft' })),
  updateLeaveApplication: (id, data) => api.patch(`/leave-applications/${id}/`, sanitizeLeaveApplicationData(data)),
  
  // File upload variants for leave applications with medical certificates
  createLeaveApplicationWithFile: (data, file) => {
    const formData = new FormData();
    const sanitizedData = sanitizeLeaveApplicationData(data);
    
    // Append all form fields (serialize nested objects like ocr_data)
    Object.keys(sanitizedData).forEach(key => {
      const value = sanitizedData[key];
      if (value !== undefined && value !== null) {
        if (key === 'ocr_data' && typeof value === 'object') {
          formData.append(key, JSON.stringify(value));
          console.log('📤 FormData append: ocr_data = JSON string');
        } else {
          formData.append(key, value);
          console.log(`📤 FormData append: ${key} = ${value}`);
        }
      }
    });

    // Fallback: ensure raw data.ocr_data gets serialized if sanitize stripped it
    if (data && data.ocr_data && typeof data.ocr_data === 'object' && !sanitizedData.ocr_data) {
      formData.append('ocr_data', JSON.stringify(data.ocr_data));
      console.log('📤 FormData append: ocr_data (fallback) = JSON string');
    }
    
    // Append the file
    formData.append('leave_medical_cert_file', file);
    console.log('📤 FormData append: leave_medical_cert_file =', file.name);
    
    return api.post('/leave-applications/', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },
  
  updateLeaveApplicationWithFile: (id, data, file) => {
    const formData = new FormData();
    const sanitizedData = sanitizeLeaveApplicationData(data);
    
    // Append all form fields (serialize nested objects like ocr_data)
    Object.keys(sanitizedData).forEach(key => {
      const value = sanitizedData[key];
      if (value !== undefined && value !== null) {
        if (key === 'ocr_data' && typeof value === 'object') {
          formData.append(key, JSON.stringify(value));
          console.log('📤 FormData append: ocr_data = JSON string');
        } else {
          formData.append(key, value);
        }
      }
    });

    // Fallback: ensure raw data.ocr_data gets serialized if sanitize stripped it
    if (data && data.ocr_data && typeof data.ocr_data === 'object' && !sanitizedData.ocr_data) {
      formData.append('ocr_data', JSON.stringify(data.ocr_data));
      console.log('📤 FormData append: ocr_data (fallback) = JSON string');
    }
    
    // Append the file
    formData.append('leave_medical_cert_file', file);
    
    return api.patch(`/leave-applications/${id}/`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },
  deleteLeaveApplication: (id) => api.delete(`/leave-applications/${id}/`),
  approveLeaveApplication: (id) => api.post(`/leave-applications/${id}/approve/`),
  cancelLeaveApplication: (id, data) => api.post(`/leave-applications/${id}/cancel/`, data),
  rejectLeaveApplication: (id, data) => api.post(`/leave-applications/${id}/reject/`, data),
  acknowledgeNSLeave: (id) => api.post(`/leave-applications/${id}/acknowledge/`),
  // Pattern B stage 1: upload flight ticket file only → creates DocumentApproval → returns doc_approval_id
  uploadFlightTicket: (id, file) => {
    const form = new FormData();
    form.append('flight_ticket_file', file);
    return api.post(`/leave-applications/${id}/upload_flight_ticket/`, form, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  // Pattern B stage 1b: employee polls for OCR results on their flight ticket
  getFlightTicketOCRStatus: (id) => api.get(`/leave-applications/${id}/flight_ticket_ocr_status/`),
  // Pattern B cleanup: discard uploaded flight ticket + DA when employee closes without submitting
  discardFlightTicketUpload: (id) => api.delete(`/leave-applications/${id}/discard_flight_ticket/`),
  // Pattern B stage 1c: trigger n8n OCR for uploaded flight ticket.
  // `file` is optional — HR re-runs load the stored DocumentApproval file.
  // Timeout must outlast the backend's 60s n8n ceiling (axios default is 30s).
  sendFlightTicketToN8N: (documentApprovalId, file, staffId, leaveApplicationId) => {
    const form = new FormData();
    if (file) form.append('file', file);
    form.append('document_approval_id', documentApprovalId);
    form.append('staff_id', staffId || '');
    form.append('leave_application_id', leaveApplicationId || '');
    return api.post('/flight-ticket/trigger-ocr/', form, {
      headers: { 'Content-Type': 'multipart/form-data' },
      timeout: 90000,
    });
  },
  // Pattern B stage 2 (or legacy): submit confirmed flight details (file optional if already uploaded)
  submitFlightDetails: (id, formData) => api.post(`/leave-applications/${id}/submit_flight_details/`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
  // HR only: amend leave dates (shorten-only) when they don't tally with the flight ticket
  amendLeaveDates: (id, data) => api.post(`/leave-applications/${id}/amend_dates/`, data),
  /** Phase 1: HR correct dates/AM–PM for External Activity, On Course, WFH (full validation server-side). */
  hrCorrectWorkArrangement: (id, data) =>
    api.post(`/leave-applications/${id}/hr_correct_work_arrangement/`, data),
  // Home Leave: calculate end date based on 30 calendar days
  calculateHomeLeaveEndDate: (startDate) => api.post('/leave-applications/calculate_home_leave_end_date/', { start_date: startDate }),
  // Home Leave: get Annual Leave consumption preview (pass startDate + endDate for accurate split)
  getHomeLeaveALPreview: (startDate, endDate) => api.get('/leave-applications/home_leave_al_preview/',
    startDate && endDate ? { params: { start_date: startDate, end_date: endDate } } : undefined),
  // Urgent Leave: calculate end date based on 14 calendar days
  calculateUrgentLeaveEndDate: (startDate) => api.post('/leave-applications/calculate_urgent_leave_end_date/', { start_date: startDate }),
  // Urgent Leave: get Annual Leave consumption preview (AL first, then Unpaid Leave)
  getUrgentLeaveALPreview: () => api.get('/leave-applications/urgent_leave_al_preview/'),
  getLeaveStatistics: () => api.get('/leave-applications/statistics/'),
  
  // NS Call-up Order document upload
  uploadNSDocument: (file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post('/ns-document/upload/', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },
  
  // Death Certificate document upload (Compassionate Leave)
  uploadDeathCertificate: (file, leaveApplicationId = null) => {
    const formData = new FormData();
    formData.append('file', file);
    if (leaveApplicationId) {
      formData.append('leave_application_id', leaveApplicationId);
    }
    return api.post('/death-certificate/upload/', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },
  deleteDeathCertificate: (documentApprovalId) => {
    return api.delete(`/death-certificate/${documentApprovalId}/delete/`);
  },

  // Driving Licence document upload (multi-class OCR)
  uploadDrivingLicence: (file, targetStaffId = null) => {
    const formData = new FormData();
    formData.append('file', file);
    if (targetStaffId) {
      formData.append('target_staff_id', targetStaffId);
    }
    return api.post('/driving-license/upload/', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },

  // Leave Balances
  getLeaveBalances: (params) => api.get('/leave-balances/', { params }),
  // FIXED: getMyLeaveBalances now internally uses eligibility filtering (same logic as getAvailableLeaveTypes)
  // This ensures users only see leave types they're eligible for, preventing confusion
  getMyLeaveBalances: (params) => api.get('/leave-balances/my_balances/', { params }),
  getAnnualLeaveYearEndPlan: () => api.get('/leave-balances/year_end_plan/'),
  getConsolidatedLeaveBalances: () => api.get('/leave-balances/consolidated_balances/'),
  getLeaveBalanceSummary: (year) => api.get('/leave-balances/summary/', { params: { year } }),
  adjustLeaveBalance: (employeeId, data) => api.post(`/leave-balances/${employeeId}/adjust/`, data),
  createInitialBalances: (staffId, year) => api.post('/leave-balances/create_initial_balances/', { staff_id: staffId, year }),
  updateLeaveBalance: (id) => api.post(`/leave-balances/${id}/update_balance/`),
  accrueLeave: (id, days) => api.post(`/leave-balances/${id}/accrue_leave/`, { days }),
  
  // New sick leave entitlement endpoints
  updateSickLeaveEntitlement: (balanceId) => api.post(`/leave-balances/${balanceId}/update_sick_leave_entitlement/`),
  bulkUpdateSickLeaveEntitlements: (data) => api.post('/leave-balances/bulk_update_sick_leave_entitlements/', data),
  recalculateEntitlement: (balanceId) => api.post(`/leave-balances/${balanceId}/recalculate_entitlement/`),
  
  // Leave statistics and special endpoints
  getLeaveStatistics: () => api.get('/leave-applications/statistics/'),
  getLeaveOverviewStatistics: (classification) => {
    const params = classification ? { classification } : {};
    return api.get('/leave-applications/overview-statistics/', { params });
  },
  getPendingLeaveApplications: (roOnly = false) => {
    const params = roOnly ? { ro_only: 'true' } : {};
    return api.get('/leave-applications/pending_approval/', { params });
  },
  getPendingLeaveCount: () =>
    api.get('/leave-applications/pending_count/'),
  getMyTeamLeaves: () => api.get('/leave-applications/my_team_leaves/'),
  getOverlappingTeamLeaves: (leaveId) => api.get(`/leave-applications/${leaveId}/overlapping_team_leaves/`),
  
  // MOM Compliance Validation Endpoints
  validateMOMCompliance: (data) => api.post('/leave-applications/validate_mom_compliance/', data),
  calculateLeaveDays: (data) => {
    // Ensure dates are in YYYY-MM-DD format for backend
    const formattedData = {
      ...data,
      start_date: toDateOrUndefined(data.start_date),
      end_date: toDateOrUndefined(data.end_date)
    };
    return api.post('/leave-applications/calculate_leave_days/', formattedData);
  },
  validateSickLeaveEligibility: (data) => api.post('/leave-applications/validate_sick_leave_eligibility/', data),
  previewLeaveApplication: (data) => api.post('/leave-applications/preview_leave_application/', data),
  proposeConsumption: (data) => {
    const formattedData = {
      ...data,
      start_date: toDateOrUndefined(data.start_date),
      end_date: toDateOrUndefined(data.end_date)
    };
    return api.post('/leave-applications/propose_consumption/', formattedData);
  },
  checkLeaveConflicts: (data) => {
    // Ensure dates are in YYYY-MM-DD format for backend
    const formattedData = {
      ...data,
      start_date: toDateOrUndefined(data.start_date),
      end_date: toDateOrUndefined(data.end_date)
    };
    return api.post('/leave-applications/check_leave_conflicts/', formattedData);
  },
  calculateMaternityLeave: (data) => api.post('/leave-applications/calculate_maternity_leave/', data),

  // HR admin initiates MC leave on behalf of employee (creates draft LeaveApplication)
  hrInitiateMcLeave: (targetStaffId, leaveType) =>
    api.post('/mc/hr-initiate/', { target_staff_id: targetStaffId, leave_type: leaveType }),

  createProvisionalHospitalisation: (data) =>
    api.post('/hospitalisation/provisional/', data),
  listProvisionalHospitalisation: () =>
    api.get('/hospitalisation/provisional/'),
  extendProvisionalHospitalisation: (applicationId, data) =>
    api.post(`/hospitalisation/provisional/${applicationId}/extend/`, data),
  cancelProvisionalHospitalisation: (applicationId, data) =>
    api.post(`/hospitalisation/provisional/${applicationId}/cancel/`, data),
  previewProvisionalHospitalisationReconcile: (applicationId, data) =>
    api.post(`/hospitalisation/provisional/${applicationId}/reconcile/preview/`, data),
  reconcileProvisionalHospitalisation: (applicationId, data) =>
    api.post(`/hospitalisation/provisional/${applicationId}/reconcile/`, data),

  // Integrations: n8n Medical Certificate OCR webhook (via backend proxy to avoid CORS)
  sendMedicalCertToN8N: async (applicationId, file) => {
    const form = new FormData();
    form.append('file', file);
    form.append('application_id', String(applicationId));
    const resp = await api.post('/medical-certificate/trigger-ocr/', form, {
      headers: { 'Content-Type': 'multipart/form-data' },
      timeout: 60000,
    });
    // Unwrap proxy envelope { success, data, message } so callers see raw n8n data at .data
    return { ...resp, data: resp.data?.data ?? resp.data };
  },

  // Integrations: n8n Child Certificate OCR webhook (via backend proxy to avoid CORS)
  sendChildCertToN8N: async (documentApprovalId, file, staffId = null) => {
    const form = new FormData();
    form.append('file', file);
    form.append('document_approval_id', String(documentApprovalId));
    if (staffId) {
      form.append('staff_id', String(staffId));
    }
    const resp = await api.post('/child-certificate/trigger-ocr/', form, {
      headers: { 'Content-Type': 'multipart/form-data' },
      timeout: 60000,
    });
    // Unwrap proxy envelope { success, data, message } so callers see raw n8n data at .data
    return { ...resp, data: resp.data?.data ?? resp.data };
  },

  // Integrations: n8n Death Certificate OCR webhook (Compassionate Leave, via backend proxy to avoid CORS)
  sendDeathCertToN8N: async (documentApprovalId, file, staffId = null) => {
    const form = new FormData();
    form.append('file', file);
    form.append('document_approval_id', String(documentApprovalId));
    if (staffId) {
      form.append('staff_id', String(staffId));
    }
    const resp = await api.post('/death-certificate/trigger-ocr/', form, {
      headers: { 'Content-Type': 'multipart/form-data' },
      timeout: 60000,
    });
    // Unwrap proxy envelope { success, data, message } so callers see raw n8n data at .data
    return { ...resp, data: resp.data?.data ?? resp.data };
  },

  // Integrations: n8n Driving Licence OCR webhook (multi-class, via backend proxy to avoid CORS)
  sendDrivingLicenceToN8N: async (documentApprovalId, file, staffId = null) => {
    const form = new FormData();
    form.append('file', file);
    form.append('document_approval_id', String(documentApprovalId));
    if (staffId) {
      form.append('staff_id', String(staffId));
    }
    const resp = await api.post('/driving-license/trigger-ocr/', form, {
      headers: { 'Content-Type': 'multipart/form-data' },
      timeout: 60000,
    });
    // Unwrap proxy envelope { success, data, message } so callers see raw n8n data at .data
    return { ...resp, data: resp.data?.data ?? resp.data };
  },

  // Integrations: n8n Marriage Certificate OCR webhook (via backend proxy to avoid CORS)
  sendMarriageCertToN8N: async (documentApprovalId, file, staffId = null) => {
    const form = new FormData();
    form.append('file', file);
    form.append('document_approval_id', String(documentApprovalId));
    if (staffId) {
      form.append('staff_id', String(staffId));
    }
    const resp = await api.post('/marriage-certificate/trigger-ocr/', form, {
      headers: { 'Content-Type': 'multipart/form-data' },
      timeout: 60000,
    });
    // Unwrap proxy envelope { success, data, message } so callers see raw n8n data at .data
    return { ...resp, data: resp.data?.data ?? resp.data };
  },

  // Integrations: n8n Qualification Certificate OCR webhook (via backend proxy to avoid CORS)
  sendQualificationCertToN8N: async (documentApprovalId, file, staffId = null, documentType = 'education_certificate') => {
    if (documentType !== 'education_certificate' && documentType !== 'professional_certificate') {
      return Promise.reject(new Error(`Invalid document type: ${documentType}. Must be 'education_certificate' or 'professional_certificate'`));
    }
    const form = new FormData();
    form.append('file', file);
    form.append('document_approval_id', String(documentApprovalId));
    form.append('document_type', documentType);
    if (staffId) {
      form.append('staff_id', String(staffId));
    }
    const resp = await api.post('/qualification/trigger-ocr/', form, {
      headers: { 'Content-Type': 'multipart/form-data' },
      // Multi-page certificates run one LLM OCR pass per page in n8n —
      // must outlast the backend's 180s n8n timeout
      timeout: 200000,
    });
    // Unwrap proxy envelope { success, data, message } so callers see raw n8n data at .data
    return { ...resp, data: resp.data?.data ?? resp.data };
  },

  // Integrations: n8n ID Card OCR webhook (via backend proxy to avoid CORS)
  sendIdCardToN8N: async (documentApprovalId, file, staffId = null, cardSide = 'front') => {
    const form = new FormData();
    form.append('file', file);
    form.append('document_approval_id', String(documentApprovalId));
    form.append('card_side', cardSide);
    if (staffId) {
      form.append('staff_id', String(staffId));
    }
    const resp = await api.post('/id-card/trigger-ocr/', form, {
      headers: { 'Content-Type': 'multipart/form-data' },
      timeout: 60000,
    });
    // Unwrap proxy envelope { success, data, message } so callers see raw n8n data at .data
    return { ...resp, data: resp.data?.data ?? resp.data };
  },

  // Integrations: n8n Bank Statement OCR webhook (via backend proxy to avoid CORS)
  sendBankStatementToN8N: async (documentApprovalId, file, staffId = null) => {
    const form = new FormData();
    form.append('file', file);
    form.append('document_approval_id', String(documentApprovalId));
    if (staffId) {
      form.append('staff_id', String(staffId));
    }
    const resp = await api.post('/bank-statement/trigger-ocr/', form, {
      headers: { 'Content-Type': 'multipart/form-data' },
      timeout: 60000,
    });
    // Unwrap proxy envelope { success, data, message } so callers see raw n8n data at .data
    return { ...resp, data: resp.data?.data ?? resp.data };
  },

  // Integrations: n8n NS Call-up Order OCR webhook (via backend proxy to avoid CORS)
  sendNSCallUpToN8N: (documentApprovalId, file, staffId = null) => {
    // Use backend proxy endpoint to avoid CORS issues
    const form = new FormData();
    form.append('file', file);
    form.append('document_approval_id', String(documentApprovalId));
    
    // Add staff_id if available
    if (staffId) {
      form.append('staff_id', String(staffId));
    }
    
    return api.post('/ns-call-up/trigger-ocr/', form, {
      headers: { 'Content-Type': 'multipart/form-data' },
      timeout: 60000,
    });
  },
 
  // Medical Certificate OCR verification data
  getMcVerificationData: (applicationId) => api.get(`/mc/verification-data/${applicationId}/`),
  // Update a single OCR field for MC during HR review
  updateMcOcrField: (payload) => api.post('/mc/update-field/', payload),
  // HR adjusts deductible OSL/HL days (AM/PM session or typed day count)
  updateMcDeduction: (payload) => api.post('/mc/update-deduction/', payload),
  
  // Send OCR data to backend after leave application submission
  sendMcOcrData: (ocrPayload) => api.post('/mc/ocr-webhook/', ocrPayload),
  
  // Medical Certificate verification actions
  verifyMcAndApproveLeave: (data) => api.post('/mc/verify-and-approve/', data),
  rejectMcAndLeave: (data) => api.post('/mc/reject/', data),
  discardMcDraft: (applicationId, params = {}) => api.delete(`/mc/draft/${applicationId}/`, { params }),
  detachMcFromApplication: (applicationId) => api.post(`/mc/detach/${applicationId}/`),
  // Cancel (soft, auditable) a verified/approved sick-leave application — keeps the
  // record + MC document for audit, restores the balance (HR only; reason required)
  cancelVerifiedMcLeave: (applicationId, cancellationReason) =>
    api.post(`/mc/cancel-verified/${applicationId}/`, { cancellation_reason: cancellationReason }),

  // Attendance
  getAttendance: (params) => api.get('/attendance/', { params }),
  getAttendanceSummary: () => api.get('/attendance/summary/'),
  getLateArrivals: () => api.get('/attendance/late_arrivals/'),
  getRealtimeAttendance: (params) => api.get('/attendance/realtime/', { params }),
  getAttendanceStatistics: (params) => api.get('/attendance/statistics/', { params }),
  getAttendanceRecords: (params) => api.get('/attendance/records/', { params }),
  getDailyAttendance: (params) => api.get('/attendance/daily/', { params }),
  // NOTE: getDailyStatistics was previously a duplicate of getDailyAttendance (same endpoint).
  // Kept for backward compat but callers should prefer getDailyAttendance + client-side stats.
  getDailyStatistics: (params) => api.get('/attendance/daily/', { params }),
  // Payroll Step 2: Attendance normalization & locks
  getAttendanceRecordsReport: (params) => api.get('/labour-cost/attendance-records/', { params }),
  getAttendanceLocks: (params) => api.get('/attendance-lock/list/', { params }),
  applyAttendanceLock: (data) => api.post('/attendance-lock/apply/', data),
  removeAttendanceLock: (data) => api.post('/attendance-lock/remove/', data),

  // PLC (Project Labour Cost) endpoints
  plcUploadLevyBill: (formData) => api.post('/plc/levy-bill/upload/', formData, { headers: { 'Content-Type': 'multipart/form-data' } }),
  plcGetLevyReport: (params) => api.get('/plc/levy-report/', { params }),
  plcGetLevyReportByTrade: (params) => api.get('/plc/levy-report/by-trade/', { params }),
  plcGetLevyReportByDepartment: (params) => api.get('/plc/levy-report/by-department/', { params }),
  plcGetSalaryReportByTrade: (params) => api.get('/plc/salary-report/by-trade/', { params }),
  plcGetSalaryReportByEmployee: (params) => api.get('/plc/salary-report/by-employee/', { params }),
  plcExportSalaryByEmployee: (params) => api.get('/plc/salary-report/by-employee/export/', { params, responseType: 'blob' }),
  plcExportSalaryReport: (params) => api.get('/plc/salary-report/export/', { params, responseType: 'blob' }),
  plcGetLevyBillList: () => api.get('/plc/levy-bill/list/'),
  plcGetLevyBillDetail: (billId) => api.get(`/plc/levy-bill/${billId}/`),
  plcGetAvailableMonths: () => api.get('/plc/available-months/'),
  plcGetDormCostReport: (params) => api.get('/plc/dorm-cost-report/', { params }),
  plcExportLevyReport: (params) => api.get('/plc/levy-report/export/', { params, responseType: 'blob' }),
  plcExportDormCostReport: (params) => api.get('/plc/dorm-cost-report/export/', { params, responseType: 'blob' }),
  plcGetCombinedReport: (params) => api.get('/plc/combined-report/', { params }),
  plcExportCombinedReport: (params) => api.get('/plc/combined-report/export/', { params, responseType: 'blob' }),
  // Single XLSX bundling Summary + Salary + Levy + MOM Levy Bill + Dormitory
  // (in finance "report by materiality" order). See backend:
  // staff_core/services/plc_workbook_export_service.py.
  plcExportWorkbook: (params) => api.get('/plc/workbook/export/', { params, responseType: 'blob' }),

  // PCP (Primary Care Plan) endpoints
  pcpProviders: () => api.get('/plc/pcp/providers/'),
  pcpUpload: (formData) => api.post('/plc/pcp/upload/', formData, { headers: { 'Content-Type': 'multipart/form-data' }, timeout: 120000 }),
  pcpSave: (uploadId, entries) => api.post(`/plc/pcp/${uploadId}/save/`, { entries }),
  pcpGetList: () => api.get('/plc/pcp/list/'),
  pcpGetDetail: (uploadId) => api.get(`/plc/pcp/${uploadId}/`),
  pcpMonthSummary: (year, month) => api.get(`/plc/pcp/month-summary/?year=${year}&month=${month}`),
  pcpVerifyMonth: (year, month) => api.post('/plc/pcp/verify-month/', { year, month }),
  pcpExportXlsx: (year, month) => api.get(`/plc/pcp/export/?year=${year}&month=${month}`, { responseType: 'blob' }),

  // Final Settlements (for mid-month resignations)
  getFinalSettlementEmployees: (params) => api.get('/final-settlement/employees/', { params }),
  calculateFinalSettlement: (data) => api.post('/final-settlement/calculate/', data),
  processFinalSettlement: (data) => api.post('/final-settlement/process/', data),
  getProjectMonitoring: (params) => api.get('/attendance/project_monitoring/', { params }),
  getWorkSiteTracking: (params) => {
    // Temporary fix: return mock data to prevent 500 errors
    return Promise.resolve({
      data: {
        work_site: { id: 1, name: 'Mock Site', type: 'office' },
        project: { code: 'MOCK-01', name: 'Mock Project', type: 'office' },
        staff_tracking: [],
        summary: { total_staff_today: 0, currently_present: 0, total_hours_today: 0 }
      }
    });
  },
  getExceptionsAlerts: (params) => {
    // Get exceptions with filtering
    return api.get('/attendance-exceptions/', { params });
  },
  getExceptionsPendingCount: () => api.get('/attendance-exceptions/pending_count/'),

  // Exceptions resolution (admin). If backend endpoints are unavailable, return a resolved promise to avoid crashes.
  updateExceptionResolution: (id, data) => {
    return api.post(`/attendance-exceptions/${id}/triage/`, data);
  },
  getExceptionAudit: (id) => {
    try {
      return api.get(`/attendance-exceptions/${id}/audit/`);
    } catch (e) {
      // Mock minimal audit trail
      return Promise.resolve({ data: { audit: [] } });
    }
  },

  // RO Exception Resolution (create clock events)
  // Note: DRF action routes preserve underscores in URLs
  getMyTeamExceptions: () => api.get('/attendance-exceptions/my_team/'),
  getPeerClockOutSuggestion: (id, params = {}) => api.get(`/attendance-exceptions/${id}/peer_suggestion/`, { params }),
  createExceptionSessions: (id, data) => api.post(`/attendance-exceptions/${id}/create_sessions/`, data),
  setExceptionClockOut: (id, data) => api.post(`/attendance-exceptions/${id}/set_clock_out/`, data),
  createExceptionLeave: (id, data) => api.post(`/attendance-exceptions/${id}/create_leave/`, data),
  confirmExceptionAbsence: (id, data) => api.post(`/attendance-exceptions/${id}/confirm_absence/`, data),
  setExceptionStatus: (id, data) => api.post(`/attendance-exceptions/${id}/set_status/`, data),
  forceExceptionClockOut: (id, data) => api.post(`/attendance-exceptions/${id}/force_clock_out/`, data),

  // Bulk Operations
  getBulkCandidates: (operation, params = {}) => api.get('/attendance/bulk_candidates/', { params: { operation, ...params } }),
  bulkClockOut: (data) => api.post('/attendance/bulk_clock_out/', data),
  previewBulkLeaveDeduction: (data) => api.post('/attendance/preview_bulk_deduction/', data),
  bulkLeaveDeduction: (data) => api.post('/attendance/bulk_leave_deduction/', data),
  
  // Clock Events
  getClockEvents: (params) => api.get('/clock-events/', { params }),
  getMyEvents: (params) => api.get('/clock-events/my_events/', { params }), // Personal events only
  getMyDailyHours: (params) => api.get('/clock-events/my-daily-hours/', { params }),
  // Self-scoped monthly OT breakdown (per-day cap + off-cap buckets + day
  // type) powering the worker's Monthly OT calendar on the Attendance page.
  getMyOvertimeBreakdown: (params) => api.get('/staff-core/overtime-monitoring/my-daily-breakdown/', { params }),
  clockIn: (data = {}) => api.post('/clock-events/', { 
    clock_event_type: 'clock_in',
    ...data 
  }),
  clockOut: (data = {}) => api.post('/clock-events/', { 
    clock_event_type: 'clock_out',
    ...data 
  }),
  getClockStatus: () => api.get('/clock-events/my_status/'),
  // Self-scoped MOM s36 rest-day streak (current + optional projection for
  // a draft WFH/External Activity application — see rest_day_streak action).
  getMyRestDayStreak: (params) => api.get('/clock-events/rest_day_streak/', { params }),
  getMyWeather: (params = {}) => api.get('/clock-events/my_weather/', { params }),
  getTodayClockEvents: () => api.get('/clock-events/today/'),
  getTodayEvents: () => api.get('/clock-events/today/'),
  
  // CRUD operations for clock events
  deleteClockEvent: (eventId) => api.delete(`/clock-events/${eventId}/`),
  updateClockEvent: (eventId, data) => api.patch(`/clock-events/${eventId}/`, data),
  bulkDeleteClockEvents: (eventIds) => api.post('/clock-events/bulk_delete/', { event_ids: eventIds }),

  // Overtime Management
  amendOvertime: (eventId, data) => api.post(`/clock-events/${eventId}/amend-overtime/`, data),
  submitOvertime: (otData) => api.post('/clock-events/submit_overtime/', otData),
  getOvertimeSummary: (params) => api.get('/clock-events/overtime_summary/', { params }),
  
  // Attendance normalization
  renormalizeAttendance: (staffId, date) => api.post(`/attendance/${staffId}/renormalize/`, { date }),
  // AttendanceRecord sessions
  getAttendanceSessions: (attendanceRecordId) => api.get(`/attendance/${attendanceRecordId}/sessions/`),
  setAttendanceSessionDeleted: (attendanceRecordId, sessionId, isDeleted, reason='') =>
    api.post(`/attendance/${attendanceRecordId}/sessions/${sessionId}/set-deleted/`, { is_deleted: isDeleted, reason }),
  updateAttendanceSession: (attendanceRecordId, sessionId, data) =>
    api.patch(`/attendance/${attendanceRecordId}/sessions/${sessionId}/`, data),
  createLeaveRecord: (staffId, date, workSiteId) =>
    api.post('/attendance/create-leave-record/', { staff_id: staffId, date, work_site_id: workSiteId }),

  // Legacy attendance endpoints (for historical data)
  legacyClockIn: () => api.post('/attendance/clock_in/'),
  legacyClockOut: (id) => api.patch(`/attendance/${id}/clock_out/`),
  
  // Site Management
  getProjects: (params) => api.get('/projects/', { params }),
  // Pass { forClockIn: true } only from the self-service Attendance picker.
  // Nightwork / Rest Day / leave / RO exception must omit it so they still
  // see the full active list (including works-order look-alikes).
  getActiveProjects: ({ forClockIn = false } = {}) =>
    api.get('/projects/active/', {
      params: forClockIn ? { for_clock_in: 'true' } : undefined,
    }),
  getNearbyProjects: (latitude, longitude, radius = 5, { forClockIn = false } = {}) =>
    api.get('/projects/nearby/', {
      params: {
        latitude,
        longitude,
        radius,
        ...(forClockIn ? { for_clock_in: 'true' } : {}),
      },
    }),
  getProject: (id) => api.get(`/projects/${id}/`),
  createProject: (data) => api.post('/projects/', data),
  updateProject: (id, data) => api.patch(`/projects/${id}/`, data),
  // Pass { forClockIn: true } from the self-service clock-in picker so the
  // backend scopes work-sites by the employee's classification (workers see
  // Factory, staff see Office under the shared-coordinate main-office sites).
  // Admin surfaces (clock-event edit, site management) omit it and see all.
  getWorkSitesForProject: (projectCode, { forClockIn = false } = {}) =>
    api.get('/work-sites/', {
      params: { project: projectCode, ...(forClockIn ? { for_clock_in: 'true' } : {}) },
    }),
  updateWorkSite: (id, data) => api.patch(`/work-sites/${id}/`, data),
  deleteProject: (id) => api.delete(`/projects/${id}/`),
  getProjectStatistics: () => api.get('/projects/statistics/'),
  getProjectRecentActivity: (id) => api.get(`/projects/${id}/recent_activity/`),
  
  // Education Management
  getEducation: (params) => api.get('/education/', { params }),
  createEducation: (data) => api.post('/education/', data),
  updateEducation: (id, data) => api.patch(`/education/${id}/`, data),
  deleteEducation: (id) => api.delete(`/education/${id}/`),
  getEducationVerificationPending: () => api.get('/education/verification_pending/'),
  downloadEducation: (id) => api.get(`/education/${id}/download/`, { responseType: 'blob' }),

  // Professional Certificates
  getCertificates: (params) => api.get('/certificates/', { params }),
  createCertificate: (data) => api.post('/certificates/', data),
  updateCertificate: (id, data) => api.patch(`/certificates/${id}/`, data),
  deleteCertificate: (id) => api.delete(`/certificates/${id}/`),
  getCertificatesExpiringSoon: () => api.get('/certificates/expiring_soon/'),
  getExpiredCertificates: () => api.get('/certificates/expired/'),
  getCertificatesByCategory: () => api.get('/certificates/by_category/'),
  downloadCertificate: (id) => api.get(`/certificates/${id}/download/`, { responseType: 'blob' }),
  
  // Document Templates (Configuration)
  getDocumentTemplates: (params = {}) => api.get('/configuration/document-templates/', { params }),
  getDocumentTypes: (params = {}) => api.get('/configuration/document-types/', { params }),
  createDocumentType: (data) => api.post('/configuration/document-types/', data),
  updateDocumentType: (id, data) => api.patch(`/configuration/document-types/${id}/`, data),
  deleteDocumentType: (id) => api.delete(`/configuration/document-types/${id}/`),
  createDocumentTemplate: (data) => {
    // Ensure required fields
    if (!data?.effective_from) {
      data = { ...data, effective_from: getTodayInSingaporeTimezone() };
    }
    // If file present, use multipart
    if (data?.docx_file instanceof File) {
      const form = new FormData();
      Object.entries(data).forEach(([k, v]) => {
        if (v === undefined || v === null) return;
        if (k === 'docx_file') form.append('docx_file', v);
        else form.append(k, typeof v === 'object' ? JSON.stringify(v) : v);
      });
      return api.post('/configuration/document-templates/', form, { headers: { 'Content-Type': 'multipart/form-data' } });
    }
    return api.post('/configuration/document-templates/', data);
  },
  updateDocumentTemplate: (id, data) => {
    if (!data?.effective_from) {
      data = { ...data, effective_from: getTodayInSingaporeTimezone() };
    }
    if (data?.docx_file instanceof File) {
      const form = new FormData();
      Object.entries(data).forEach(([k, v]) => {
        if (v === undefined || v === null) return;
        if (k === 'docx_file') form.append('docx_file', v);
        else form.append(k, typeof v === 'object' ? JSON.stringify(v) : v);
      });
      return api.patch(`/configuration/document-templates/${id}/`, form, { headers: { 'Content-Type': 'multipart/form-data' } });
    }
    return api.patch(`/configuration/document-templates/${id}/`, data);
  },
  deleteDocumentTemplate: (id) => api.delete(`/configuration/document-templates/${id}/`),
  publishDocumentTemplate: (id) => api.post(`/configuration/document-templates/${id}/publish/`),
  archiveDocumentTemplate: (id) => api.post(`/configuration/document-templates/${id}/archive/`),
  
  // Work Experience
  getWorkExperience: (params) => api.get('/work-experience/', { params }),
  createWorkExperience: (data) => api.post('/work-experience/', data),
  updateWorkExperience: (id, data) => api.patch(`/work-experience/${id}/`, data),
  deleteWorkExperience: (id) => api.delete(`/work-experience/${id}/`),
  getWorkExperienceByIndustry: () => api.get('/work-experience/by_industry/'),
  getConstructionExperienceSummary: (staffId) => api.get(`/wpol/construction-experience-summary/${staffId}/`),
  
  // Public Holidays
  getPublicHolidays: (params) => api.get('/holidays/', { params }),
  getCurrentYearHolidays: () => api.get('/holidays/current_year/'),
  getUpcomingHolidays: () => api.get('/holidays/upcoming/'),
  
  // Employee Schedule Management
  getEmployeeSchedules: (params = {}) => api.get('/employee-schedules/', { params }),
  createEmployeeSchedule: (data) => api.post('/employee-schedules/', data),
  updateEmployeeSchedule: (id, data) => api.put(`/employee-schedules/${id}/`, data),
  deleteEmployeeSchedule: (id) => api.delete(`/employee-schedules/${id}/`),
  getCurrentScheduleAssignments: () => api.get('/employee-schedules/current_assignments/'),
  bulkAssignSchedules: (data) => api.post('/employee-schedules/bulk_assign/', data),
  getUnassignedEmployees: () => api.get('/employee-schedules/unassigned_employees/'),
  getDepartmentScheduleSummary: () => api.get('/employee-schedules/department_summary/'),
  extendScheduleAssignment: (id, data) => api.post(`/employee-schedules/${id}/extend_assignment/`, data),

  // Schedule Override Management (alternate Saturdays, ad-hoc overrides)
  getScheduleOverrides: (params = {}) => api.get('/schedule-overrides/', { params }),
  createScheduleOverride: (data) => api.post('/schedule-overrides/', data),
  updateScheduleOverride: (id, data) => api.patch(`/schedule-overrides/${id}/`, data),
  deleteScheduleOverride: (id) => api.delete(`/schedule-overrides/${id}/`),
  bulkCreateScheduleOverrides: (data) => api.post('/schedule-overrides/bulk_create/', data),
  bulkDeleteScheduleOverrides: (data) => api.post('/schedule-overrides/bulk_delete/', data),
  refreshScheduleOverrideAttendance: (id, data = {}) => api.post(`/schedule-overrides/${id}/refresh_attendance/`, data),
  refreshScheduleOverrideAttendanceByDate: (data) => api.post('/schedule-overrides/refresh_date/', data),

  // Nightwork Request Management
  getNightworkRequests: (params) => api.get('/nightwork-requests/', { params }),
  getNightworkRequest: (id) => api.get(`/nightwork-requests/${id}/`),
  createNightworkRequest: (data) => api.post('/nightwork-requests/', data),
  updateNightworkRequest: (id, data) => api.put(`/nightwork-requests/${id}/`, data),
  deleteNightworkRequest: (id) => api.delete(`/nightwork-requests/${id}/`),
  submitNightworkRequest: (id) => api.post(`/nightwork-requests/${id}/submit/`),
  approveNightworkRequest: (id, data) => api.post(`/nightwork-requests/${id}/approve/`, data),
  rejectNightworkRequest: (id, data) => api.post(`/nightwork-requests/${id}/reject/`, data),
  returnNightworkRequest: (id, data) => api.post(`/nightwork-requests/${id}/return_for_amendment/`, data),
  cancelNightworkRequest: (id, data) => api.post(`/nightwork-requests/${id}/cancel/`, data),
  amendNightworkRequest: (id) => api.post(`/nightwork-requests/${id}/amend/`),
  hrNightworkAdminAction: (id, data) => api.post(`/nightwork-requests/${id}/hr_admin_action/`, data),
  getNightworkPendingApproval: () => api.get('/nightwork-requests/pending_approval/'),
  getNightworkMyRequests: () => api.get('/nightwork-requests/my_requests/'),
  getNightworkAllRequests: () => api.get('/nightwork-requests/all_requests/'),
  getNightworkActiveWorkers: () => api.get('/nightwork-requests/active_workers/'),
  getNightworkSafetyPersonnel: () => api.get('/nightwork-requests/safety_personnel/'),
  getNightworkAffectedDates: (id) => api.get(`/nightwork-requests/${id}/affected_dates/`),
  getNightworkAmendmentDiff: (id) => api.get(`/nightwork-requests/${id}/amendment_diff/`),
  getNightworkMyAssignments: () => api.get('/nightwork-requests/my_assignments/'),
  getNightworkDashboardSummary: () => api.get('/nightwork-requests/dashboard_summary/'),

  // Rest Day / Sunday Work Request Management
  getRestDayWorkRequests: (params) => api.get('/restday-work-requests/', { params }),
  getRestDayWorkRequest: (id) => api.get(`/restday-work-requests/${id}/`),
  createRestDayWorkRequest: (data) => api.post('/restday-work-requests/', data),
  updateRestDayWorkRequest: (id, data) => api.put(`/restday-work-requests/${id}/`, data),
  deleteRestDayWorkRequest: (id) => api.delete(`/restday-work-requests/${id}/`),
  submitRestDayWorkRequest: (id) => api.post(`/restday-work-requests/${id}/submit/`),
  approveRestDayWorkRequest: (id, data) => api.post(`/restday-work-requests/${id}/approve/`, data),
  rejectRestDayWorkRequest: (id, data) => api.post(`/restday-work-requests/${id}/reject/`, data),
  cancelRestDayWorkRequest: (id, data) => api.post(`/restday-work-requests/${id}/cancel/`, data),
  amendRestDayWorkRequest: (id) => api.post(`/restday-work-requests/${id}/amend/`),
  hrRestDayWorkAdminAction: (id, data) => api.post(`/restday-work-requests/${id}/hr_admin_action/`, data),
  getRestDayWorkPendingApproval: () => api.get('/restday-work-requests/pending_approval/'),
  getRestDayWorkMyRequests: () => api.get('/restday-work-requests/my_requests/'),
  getRestDayWorkAllRequests: () => api.get('/restday-work-requests/all_requests/'),
  getRestDayWorkActiveWorkers: () => api.get('/restday-work-requests/active_workers/'),
  getRestDayWorkSafetyPersonnel: () => api.get('/restday-work-requests/safety_personnel/'),
  getRestDayWorkAmendmentDiff: (id) => api.get(`/restday-work-requests/${id}/amendment_diff/`),
  getRestDayWorkMyAssignments: () => api.get('/restday-work-requests/my_assignments/'),
  getRestDayWorkDashboardSummary: () => api.get('/restday-work-requests/dashboard_summary/'),
  checkRestDayWorkCompliance: (data) => api.post('/restday-work-requests/compliance_check/', data),

  // Worker Trade Transfer API methods
  getTradeTransferRequests: (params) => api.get('/trade-transfer-requests/', { params }),
  getTradeTransferRequest: (id) => api.get(`/trade-transfer-requests/${id}/`),
  createTradeTransferRequest: (data) => api.post('/trade-transfer-requests/', data),
  hrInitiateTradeTransfer: (data) => api.post('/trade-transfer-requests/hr-initiate/', data),
  getTradeTransferAllTrades: () => api.get('/trade-transfer-requests/all-trades/'),
  cancelTradeTransferRequest: (id, data) => api.post(`/trade-transfer-requests/${id}/cancel/`, data),
  getTradeTransferMyRequests: () => api.get('/trade-transfer-requests/my_requests/'),
  getTradeTransferTransferableWorkers: () => api.get('/trade-transfer-requests/transferable-workers/'),
  getTradeTransferLedTrades: () => api.get('/trade-transfer-requests/led-trades/'),
  getTradeTransferEligibleReportingOfficers: (tradeId) => api.get(
    '/trade-transfer-requests/eligible-reporting-officers/',
    { params: { trade_id: tradeId } },
  ),
  getTradeTransferInbox: () => api.get('/trade-transfer-requests/inbox/'),
  getTradeTransferItem: (itemId) => api.get(`/trade-transfer-requests/item/${itemId}/`),
  decideTradeTransferItem: (itemId, data) => api.post(`/trade-transfer-requests/item/${itemId}/decide/`, data),
  getTradeTransferDashboardSummary: () => api.get('/trade-transfer-requests/dashboard-summary/'),

  // Passport Custody Request API methods
  getPassportRequests: (params) => api.get('/passport-requests/', { params }),
  getPassportRequest: (id) => api.get(`/passport-requests/${id}/`),
  createPassportRequest: (data) => api.post('/passport-requests/', data),
  approvePassportRequest: (id) => api.post(`/passport-requests/${id}/approve/`),
  rejectPassportRequest: (id, data) => api.post(`/passport-requests/${id}/reject/`, data),
  cancelPassportRequest: (id, data) => api.post(`/passport-requests/${id}/cancel/`, data),
  reassignPassportCollector: (id, data) => api.post(
    `/passport-requests/${id}/reassign-collector/`,
    data,
  ),
  reportPassportCollectorUnavailable: (id, data) => api.post(
    `/passport-requests/${id}/collector-unavailable/`,
    data,
  ),
  acknowledgePassportItems: (data) => api.post('/passport-requests/acknowledge/', data),
  getPassportMyItems: () => api.get('/passport-requests/my-items/'),
  getPassportRequestableWorkers: () => api.get('/passport-requests/requestable-workers/'),
  getPassportCollectors: () => api.get('/passport-requests/collectors/'),
  getPassportCollectionAvailability: (collectionDate) => api.get(
    '/passport-requests/collection-availability/',
    { params: { collection_date: collectionDate } },
  ),
  getPassportUpcomingLeaves: (params) => api.get('/passport-requests/upcoming-leaves/', { params }),
  getPassportSummary: () => api.get('/passport-requests/summary/'),

  // Course / Training Requests
  getCourseRequests: (params) => api.get('/course-requests/', { params }),
  getCourseRequest: (id) => api.get(`/course-requests/${id}/`),
  createCourseRequest: (data) => api.post('/course-requests/', data),
  getCourseRequestCapabilities: () => api.get('/course-requests/capabilities/'),
  getCourseRequestMyRequests: () => api.get('/course-requests/my-requests/'),
  getCourseRequestPendingHod: () => api.get('/course-requests/pending-hod/'),
  getCourseRequestHrInbox: (params) => api.get('/course-requests/hr-inbox/', { params }),
  getCourseRequestStatusSummary: () => api.get('/course-requests/status-summary/'),
  getCourseRequestBondManagement: () => api.get('/course-requests/bond-management/'),
  getCourseNominatableWorkers: () => api.get('/course-requests/nominatable-workers/'),
  approveCourseRequestHod: (id, data) => api.post(`/course-requests/${id}/approve-hod/`, data),
  rejectCourseRequestHod: (id, data) => api.post(`/course-requests/${id}/reject-hod/`, data),
  approveCourseRequestHr: (id, data) => api.post(`/course-requests/${id}/approve-hr/`, data),
  rejectCourseRequestHr: (id, data) => api.post(`/course-requests/${id}/reject-hr/`, data),
  cancelCourseRequest: (id, data) => api.post(`/course-requests/${id}/cancel/`, data),
  issueCourseAgreement: (id, data) => api.post(`/course-requests/${id}/issue-agreement/`, data),
  acknowledgeCourseAgreement: (id, data) => api.post(`/course-requests/${id}/acknowledge-agreement/`, data),
  downloadCourseAgreement: (id) => api.get(`/course-requests/${id}/agreement-document/`, { responseType: 'blob' }),
  confirmCourseRequestBooking: (id, formData) => api.post(
    `/course-requests/${id}/confirm-booking/`,
    formData,
    { headers: { 'Content-Type': 'multipart/form-data' } },
  ),
  completeCourseRequest: (id, formData) => api.post(
    `/course-requests/${id}/complete/`,
    formData,
    { headers: { 'Content-Type': 'multipart/form-data' } },
  ),

  getWorkPassCapabilities: () => api.get('/work-pass-decisions/capabilities/'),
  getWorkPassQueue: (tab) => api.get('/work-pass-decisions/queue/', { params: { tab } }),
  getWorkPassDecision: (id) => api.get(`/work-pass-decisions/${id}/`),
  recommendWorkPassDecision: (id, data) => api.post(`/work-pass-decisions/${id}/recommend/`, data),
  approveWorkPassDecision: (id, data) => api.post(`/work-pass-decisions/${id}/approve/`, data),
  returnWorkPassDecision: (id, data) => api.post(`/work-pass-decisions/${id}/return/`, data),
  resolveWorkPassException: (id, data) => api.post(`/work-pass-decisions/${id}/resolve-exception/`, data),

  getTerminationRequestCapabilities: () => api.get('/termination-requests/capabilities/'),
  getTerminationRequests: (params) => api.get('/termination-requests/', { params }),
  getTerminationRequest: (id) => api.get(`/termination-requests/${id}/`),
  getTerminationEligibleWorkers: () => api.get('/termination-requests/eligible-workers/'),
  createTerminationRequest: (data) => api.post('/termination-requests/', data),
  recordWorkerIntent: (data) => api.post('/termination-requests/worker-intent/', data),
  correctTerminationNotice: (id, data) => api.post(`/termination-requests/${id}/correct-notice/`, data),
  getResignationNotice: (id, language) => api.get(`/termination-requests/${id}/notice/`, { params: { language } }),
  acknowledgeResignationNotice: (id, data) => api.post(`/termination-requests/${id}/acknowledge/`, data),
  disputeResignationNotice: (id, data) => api.post(`/termination-requests/${id}/dispute/`, data),
  departmentAcknowledgeTermination: (id, data) => api.post(`/termination-requests/${id}/department-acknowledge/`, data),
  departmentReturnTermination: (id, data) => api.post(`/termination-requests/${id}/department-return/`, data),
  cancelTerminationRequest: (id, data) => api.post(`/termination-requests/${id}/cancel/`, data),
  downloadResignationReceiptPdf: (id) => api.get(`/termination-requests/${id}/receipt-pdf/`, { responseType: 'blob' }),

  // One-time permanent passport return campaign
  getPassportReturnRecords: () => api.get('/passport-returns/'),
  getPassportReturnSummary: () => api.get('/passport-returns/summary/'),
  getPassportReturnRosterPreview: () => api.get('/passport-returns/roster-preview/'),
  getPassportReturnEligibleWorkers: () => api.get('/passport-returns/eligible-workers/'),
  getPassportReturnHRWitnesses: () => api.get('/passport-returns/hr-witnesses/'),
  registerPassportReturnWorker: (data) => api.post('/passport-returns/', data),
  seedPassportReturnRoster: (data) => api.post('/passport-returns/seed-roster/', data),
  launchPassportReturnCampaign: (data) => api.post('/passport-returns/launch/', data),
  markPassportReturnReady: (id) => api.post(`/passport-returns/${id}/mark-ready/`, {}),
  markPassportReturnHeldReady: () => api.post('/passport-returns/mark-ready-held/', {}),
  handoverPassportReturn: (id) => api.post(`/passport-returns/${id}/handover/`, {}),
  receivePassportReturnIntoCustody: (id) => api.post(`/passport-returns/${id}/receive-into-custody/`, {}),
  acknowledgePassportReturn: (id, data) => api.post(`/passport-returns/${id}/acknowledge/`, data),
  downloadPassportReturnReceiptPdf: (id) =>
    api.get(`/passport-returns/${id}/receipt-pdf/`, { responseType: 'blob' }),
  recordPassportReturnException: (id, data) => api.post(`/passport-returns/${id}/exception/`, data),

  // Overseas travel notices (rest-day / personal-time — notification only)
  getTravelNoticeBootstrap: () => api.get('/overseas-travel-notices/bootstrap/'),
  getTravelNoticeWindowPreview: (params) =>
    api.get('/overseas-travel-notices/window-preview/', { params }),
  getTravelNotices: (params = {}) => api.get('/overseas-travel-notices/', { params }),
  getMyActiveTravelNotices: () => api.get('/overseas-travel-notices/my-active/'),
  getTravelNoticeConcurrentAbsence: (params) =>
    api.get('/overseas-travel-notices/concurrent-absence/', { params }),
  createTravelNotice: (data) => api.post('/overseas-travel-notices/', data),
  updateTravelNotice: (id, data) => api.patch(`/overseas-travel-notices/${id}/`, data),
  cancelTravelNotice: (id) => api.post(`/overseas-travel-notices/${id}/cancel/`),
  confirmTravelNoticeReturn: (id) =>
    api.post(`/overseas-travel-notices/${id}/confirm-return/`),

  getWorkScheduleTemplates: () => api.get('/configuration/work-schedule-templates/'),
  getAllStaff: () => api.get('/staff/'),
  getAllDepartments: () => api.get('/departments/'),

  // Geofence Compliance API methods
  getGeofenceViolations: (params = {}) => api.get('/attendance/geofence_violations/', { params }),
  getComplianceStatistics: (params = {}) => api.get('/attendance/compliance_statistics/', { params }),
  getGeofenceMapData: (params = {}) => api.get('/attendance/geofence_map_data/', { params }),
  getEmployeeJourney: (params = {}) => api.get('/attendance/employee_journey/', { params }),
  getFraudAnomalies: (params = {}) => api.get('/attendance/fraud_anomalies/', { params }),

  // Site weather + heat/UV advisory (NEA real-time)
  getSiteWeather: (params = {}) => api.get('/attendance/site_weather/', { params }),

  // Project geofence management
  getProjectClusterCentroid: (projectId, params = {}) => api.get(`/projects/${projectId}/cluster-centroid/`, { params }),
  updateProjectLocation: (projectId, data) => api.patch(`/projects/${projectId}/update-location/`, data),
  
  // DRC Dashboard
  getDRCDashboardStatus: () => api.get('/configuration/drc-dashboard-status/'),
  
  // Turnover Metrics
  getTurnoverMetrics: () => api.get('/staff/turnover_metrics/'),
  
  // Overtime Monitoring (legacy - uses pre-aggregated data)
  getOvertimeStats: () => api.get('/staff_core/overtime-monitoring/stats/'),
  getOvertimeAlerts: () => api.get('/staff-core/overtime-monitoring/alerts/'),
  
  // Real-time Overtime Monitoring (ClockEvent-based)
  // ``timeout: 0`` mirrors the OvertimeMonitoring page (see
  // ``frontend/src/pages/OvertimeMonitoring/hooks/useOvertimeData.js``):
  // the backend kernel here (AR-sourced cap-eligible OT + live LCS
  // overlay for un-normalised current-month days) is genuinely
  // long-running -- 30-60 s is normal on a cold snapshot. The default
  // 30 s axios cap was killing the AdminDashboard request before the
  // server returned, leaving the 72-Hour Overtime Monitoring panel
  // permanently zeroed out. A partial result is meaningless; let the
  // request run to completion. The browser/TCP layer still aborts a
  // truly hung connection.
  getRealtimeOvertimeStats: () => api.get('/staff-core/overtime-monitoring/realtime-stats/', { timeout: 0 }),
  getEmployeesOvertimeStatus: (params) => api.get('/staff-core/overtime-monitoring/employees/', { params }),
  // Async manual refresh — enqueues a Celery snapshot rebuild and returns
  // 202 immediately (avoids the synchronous force-refresh 524 on a cold
  // cache). Poll getOvertimeCacheStatus until snapshot_status === 'ready'.
  refreshEmployeesOvertimeSnapshot: (data) => api.post('/staff-core/overtime-monitoring/employees/refresh/', data),
  getOvertimeCacheStatus: (params) => api.get('/staff-core/overtime-monitoring/cache-status/', { params }),
  acknowledgeOvertimeAlert: (alertId, data) => api.post(`/staff-core/overtime-monitoring/alerts/${alertId}/acknowledge/`, data),
  getEmployeeOvertimeHistory: (staffId) => api.get(`/staff-core/overtime-monitoring/employee/${staffId}/history/`),
  getOvertimeComplianceReport: (params) => api.get('/staff-core/overtime-monitoring/compliance-report/', { params }),
  
  // Rest Day Interval Monitoring (MOM Employment Act s36 - 12-day limit)
  getRestDayMonitoringStats: () => api.get('/staff-core/rest-day-monitoring/stats/'),
  getRestDayEmployeesStatus: (params) => api.get('/staff-core/rest-day-monitoring/employees/', { params }),
  getRestDayActiveAlerts: (params) => api.get('/staff-core/rest-day-monitoring/alerts/', { params }),
  acknowledgeRestDayAlert: (alertId, data) => api.post(`/staff-core/rest-day-monitoring/alerts/${alertId}/acknowledge/`, data),
  resolveRestDayAlert: (alertId, data) => api.post(`/staff-core/rest-day-monitoring/alerts/${alertId}/resolve/`, data),
  getRestDayEmployeeDetail: (staffId) => api.get(`/staff-core/rest-day-monitoring/employee/${staffId}/detail/`),
  getRestDayNotificationCoverage: (params) => api.get('/staff-core/rest-day-monitoring/notification-coverage/', { params }),

  // Manager-scoped ("My Team") compliance monitoring — RO / HOD span of
  // control. Same payload shape as the admin endpoints above, but the
  // backend restricts the roster to the requesting manager's own people.
  getMyTeamOvertimeStatus: (params) => api.get('/staff-core/overtime-monitoring/my-team/employees/', { params, timeout: 0 }),
  getMyTeamOvertimeEligibility: () => api.get('/staff-core/overtime-monitoring/my-team/eligibility/'),
  getMyTeamRestDayStats: () => api.get('/staff-core/rest-day-monitoring/my-team/stats/'),
  getMyTeamRestDayStatus: (params) => api.get('/staff-core/rest-day-monitoring/my-team/employees/', { params }),
  
  // Notifications
  getNotifications: (params) => api.get('/notifications/', { params }),
  getUnreadNotificationCount: () => api.get('/notifications/unread_count/'),
  markNotificationAsRead: (id) => api.post(`/notifications/${id}/mark_read/`),
  markAllNotificationsAsRead: (category = null) => api.post('/notifications/mark_all_read/', category ? { category } : {}),
  markNotificationResolved: (id) => api.post(`/notifications/${id}/mark_resolved/`),
  getNotificationsByCategory: () => api.get('/notifications/by_category/'),
  
  // Notification Preferences
  getNotificationPreferences: () => api.get('/notification-preferences/'),
  updateNotificationPreferences: (preferences) => api.patch('/notification-preferences/', { preferences }),
  updateBirthdayOptOut: (optOut) => api.patch('/notification-preferences/birthday-opt-out/', { opt_out: optOut }),

  // System Notification Channel Defaults (Admin)
  getNotificationChannelDefaults: () => api.get('/admin/notification-channel-defaults/'),
  updateNotificationChannelDefault: (notificationType, data) =>
    api.put(`/admin/notification-channel-defaults/${notificationType}/`, data),
  
  // CV Generation API
  // Templates
  getCVTemplates: (params = {}) => api.get('/admin/cv/templates/', { params }),
  getCVTemplate: (id) => api.get(`/admin/cv/templates/${id}/`),
  
  // Project Involvement
  getProjectInvolvements: (params = {}) => api.get('/admin/cv/project-involvement/', { params }),
  getProjectInvolvement: (id) => api.get(`/admin/cv/project-involvement/${id}/`),
  createProjectInvolvement: (data) => api.post('/admin/cv/project-involvement/', data),
  updateProjectInvolvement: (id, data) => api.patch(`/admin/cv/project-involvement/${id}/`, data),
  deleteProjectInvolvement: (id) => api.delete(`/admin/cv/project-involvement/${id}/`),
  syncProjectInvolvement: (staffId) => api.post(`/admin/cv/project-involvement/sync/${staffId}/`),
  syncAllProjectInvolvements: () => api.post('/admin/cv/project-involvement/sync_all/'),
  getProjectInvolvementSyncSummary: (staffId) => api.get(`/admin/cv/project-involvement/sync_summary/${staffId}/`),
  getEmployeesWithoutProjectData: () => api.get('/admin/cv/project-involvement/employees_without_data/'),
  
  // CV Generation
  generateCV: (data) => api.post('/admin/cv/generate/', data),
  generateBatchCVs: (data) => api.post('/admin/cv/generate-batch/', data),
  previewCVData: (staffId) => api.get(`/admin/cv/preview/${staffId}/`),
  previewCVHTML: (staffId, templateId) => api.get(`/admin/cv/preview/${staffId}/${templateId}/`),
  getCVMergeFields: () => api.get('/admin/cv/merge-fields/'),
  getBatchDataQuality: (staffIds = []) => api.get('/admin/cv/data-quality/', {
    params: staffIds.length > 0 ? { staff_ids: staffIds.join(',') } : {}
  }),
  getActiveCVTemplates: () => api.get('/admin/cv/templates/active/'),
  
  // CV Generation History
  getCVGenerationLogs: (params = {}) => api.get('/admin/cv/generation-logs/', { params }),
  getCVGenerationLog: (id) => api.get(`/admin/cv/generation-logs/${id}/`),
  downloadCV: (logId) => api.get(`/admin/cv/generation-logs/${logId}/download/`, { responseType: 'blob' }),
  downloadCVAsDocx: (data) => api.post('/admin/cv/generation-logs/download-docx/', data, { responseType: 'blob' }),

  // Training Records
  changeTrainingRecordStatus: (recordId, newStatus, notes = '') =>
    api.post(`/training-records/${recordId}/change_status/`, { status: newStatus, notes }),

  // Document Approval (confidential) — used for flight ticket HR verification
  approveDocument: (documentApprovalId, data = {}) =>
    api.post(`/confidential/documents/${documentApprovalId}/approve/`, data),
  rejectDocument: (documentApprovalId, data) =>
    api.post(`/confidential/documents/${documentApprovalId}/reject/`, data),
  getFlightTicketDocument: (documentApprovalId) =>
    api.get(`/confidential/documents/${documentApprovalId}/`),
  downloadDocumentFile: (documentApprovalId) =>
    api.get(`/confidential/documents/${documentApprovalId}/download/`, { responseType: 'blob' }),

  getFlightTicketVerificationData: (documentApprovalId) =>
    api.get(`/flight-ticket/verification-data/${documentApprovalId}/`),

  // Home Leave Extension
  getExtensionEligibility: (leaveApplicationId) =>
    api.get(`/leave-applications/${leaveApplicationId}/extension_eligibility/`),
  getExtensionPreview: (leaveApplicationId, newEndDate) =>
    api.get(`/leave-applications/${leaveApplicationId}/extension_preview/`, {
      params: { new_end_date: newEndDate },
    }),
  requestExtension: (leaveApplicationId, data) =>
    api.post(`/leave-applications/${leaveApplicationId}/request_extension/`, data),

  // Generic HTTP verbs — used by pages that hit endpoints directly by path
  // (e.g. GovernmentReimbursement, ProjectLabourCostReport).
  get: (url, config) => api.get(url, config),
  post: (url, data, config) => api.post(url, data, config),
  put: (url, data, config) => api.put(url, data, config),
  patch: (url, data, config) => api.patch(url, data, config),
  delete: (url, config) => api.delete(url, config),

  // ---- Government Relations: NS Make-Up Pay ----
  getNSMakeupClaims: (params) => api.get('/ns-makeup-pay/', { params }),
  getNSMakeupDashboard: (params) => api.get('/ns-makeup-pay/dashboard_summary/', { params }),
  getNSMakeupTracker: (params) => api.get('/ns-makeup-pay/ns_makeup_tracker/', { params }),
  recordNSMakeupFromLeave: (data) => api.post('/ns-makeup-pay/record_from_leave/', data),
  createNSMakeupClaim: (data) => api.post('/ns-makeup-pay/', data),
  updateNSMakeupClaim: (id, data) => api.patch(`/ns-makeup-pay/${id}/`, data),
  deleteNSMakeupClaim: (id) => api.delete(`/ns-makeup-pay/${id}/`),
  previewNSMakeupPay: (data) => api.post('/ns-makeup-pay/calculate_preview/', data),
  updateNSMakeupStatus: (id, data) => api.post(`/ns-makeup-pay/${id}/update_status/`, data),

  // ---- Government Relations: Leave Reimbursement Tracker ----
  getReimbursementTracker: (params) => api.get('/government-reimbursement/reimbursement_tracker/', { params }),
  recordReimbursementClaim: (data) => api.post('/government-reimbursement/record_claim/', data),
  getReimbursementSubmissionDetail: (leaveApplicationId) => api.get('/government-reimbursement/submission_detail/', { params: { leave_application_id: leaveApplicationId } }),

  // ---- Government Relations: Levy Waiver ----
  getLevyWaivers: (params) => api.get('/levy-waiver/', { params }),
  getLevyWaiverDashboard: (params) => api.get('/levy-waiver/dashboard_summary/', { params }),
  getEligibleLevyBillEntries: (staffId) =>
    api.get('/levy-waiver/eligible_bill_entries/', { params: { staff_id: staffId } }),

  // ---- Government Relations: Levy Waiver Tracker (derived from approved leave) ----
  getLevyWaiverTracker: (params) => api.get('/levy-waiver/levy_waiver_tracker/', { params }),
  recordLevyWaiver: (data) => api.post('/levy-waiver/record_waiver/', data),
  getLevyWaiverSubmissionDetail: (leaveApplicationId) =>
    api.get('/levy-waiver/submission_detail/', { params: { leave_application_id: leaveApplicationId } }),
  submitLevyWaiverToMom: (id, data) => api.post(`/levy-waiver/${id}/submit_to_mom/`, data),
  updateLevyWaiverStatus: (id, data) => api.post(`/levy-waiver/${id}/update_status/`, data),
};

// System Monitoring & Audit Center
export const monitoringAPI = {
  getTaskExecutions: (params = {}) =>
    api.get('/admin/staff_core/celerytaskexecution/', { params }),
  getCurrentHealth: () =>
    api.get('/admin/staff_core/systemhealthcheck/current/'),
  getSystemAlerts: (params = {}) =>
    api.get('/admin/staff_core/systemalert/', { params }),
  getAuditLogs: (params = {}) =>
    api.get('/admin/staff_core/auditlog/', { params }),
  getTaskEffects: (taskExecutionId, params = {}) =>
    api.get(`/admin/staff_core/celerytaskexecution/${taskExecutionId}/effects/`, { params }),
};

// Confidential API
export const confidentialAPI = {
  // Generic GET method for confidential endpoints
  get: (url, config) => api.get(url, config),
  
  // Payroll payment files
  getPayrollBankFilePreview: (payrollRunId, classification, bank) =>
    api.get(`/confidential/payroll-runs/${payrollRunId}/bank-file-preview/`, {
      params: {
        classification,
        ...(bank ? { bank } : {}),
      },
    }),
  downloadPayrollBankFile: (
    payrollRunId,
    classification,
    bank,
    branchCodesReviewed = false,
  ) =>
    api.get(`/confidential/payroll-runs/${payrollRunId}/bank-file/`, {
      params: {
        classification,
        bank,
        branch_codes_reviewed: branchCodesReviewed,
      },
      responseType: 'blob',
      timeout: 60000,
    }),
  getPayrollBankConfigurations: () =>
    api.get('/confidential/payroll-bank-configurations/'),
  createPayrollBankConfiguration: (data) =>
    api.post('/confidential/payroll-bank-configurations/', data),
  updatePayrollBankConfiguration: (id, data) =>
    api.patch(`/confidential/payroll-bank-configurations/${id}/`, data),

  // Payslips
  getPayslips: (params) => api.get('/confidential/payslips/', { params }),
  getPayslip: (id) => api.get(`/confidential/payslips/${id}/`),
  downloadPayslip: (id) => api.get(`/confidential/payslips/${id}/download/`, { responseType: 'blob' }),
  recordPayslipDownload: (id) => api.post(`/confidential/payslips/${id}/download/`),
  getRecentPayslips: () => api.get('/confidential/payslips/recent_payslips/'),
  getCurrentYearPayslips: () => api.get('/confidential/payslips/current_year/'),
  getPayslipStatistics: () => api.get('/confidential/payslips/statistics/'),

  // Self-service payslips (served from EmployeePayroll on approved/paid runs)
  getMyPayslips: (params) => api.get('/confidential/employee-payrolls/my-payslips/', { params }),
  getMyPayslipDetail: (id) => api.get(`/confidential/employee-payrolls/${id}/my-payslip-detail/`),
  downloadMyPayslipPdf: (id) => api.get(`/confidential/employee-payrolls/${id}/payslip-pdf/`, { responseType: 'blob' }),
  // Bulk export: combine several of the employee's own payslips into one PDF
  // (e.g. "last 6 months" for a bank / government submission). GET so it passes
  // the confidential permission gate for worker self-service; ids are scoped
  // server-side to the caller's visible payslips.
  downloadMyPayslipsBundle: (ids) => api.get('/confidential/employee-payrolls/my-payslips-bundle/', {
    params: { ids: (ids || []).join(',') },
    responseType: 'blob',
  }),
  // HR/Finance pre-release spot check — audited as a preview, not a download
  previewPayslipPdf: (id) => api.get(`/confidential/employee-payrolls/${id}/payslip-pdf/`, { params: { preview: 'true' }, responseType: 'blob' }),
  // Mint a short-lived signed token so the PDF can be shown inline in an
  // <iframe> (no bearer header possible). The browser's PDF viewer then names
  // any download from the real Content-Disposition filename instead of a blob UUID.
  getPayslipPreviewToken: (id) => api.get(`/confidential/employee-payrolls/${id}/payslip-pdf-token/`, { params: { preview: 'true' } }),
  payslipPdfInlineUrl: (id, token) =>
    `${API_BASE_URL}/confidential/employee-payrolls/${id}/payslip-pdf/?preview=true&token=${encodeURIComponent(token)}`,
  
  // HR Profiles
  getHRProfiles: (params) => api.get('/confidential/hr-profiles/', { params }),
  getHRProfile: (id) => api.get(`/confidential/hr-profiles/${id}/`),
  getMyHRProfile: () => api.get('/confidential/hr-profiles/me/'),
  updateMyHRProfile: (data) => api.patch('/confidential/hr-profiles/me/', data),
  createHRProfile: (data) => api.post('/confidential/hr-profiles/', data),
  updateHRProfile: (id, data) => api.patch(`/confidential/hr-profiles/${id}/`, data),
  getCountryOptions: () => api.get('/confidential/hr-profiles/country-options/'),
  getHRProfileStatistics: () => api.get('/confidential/hr-profiles/statistics/'),
  getPassExpiryDashboard: () => api.get('/confidential/hr-profiles/pass-expiry-dashboard/'),
  getComplianceExpiryDashboard: () => api.get('/confidential/hr-profiles/compliance-expiry-dashboard/'),
  getHRWorkPassDecisions: (params) => api.get('/confidential/work-pass/decisions/', { params }),
  getHRWorkPassDecision: (id) => api.get(`/confidential/work-pass/decisions/${id}/`),
  getHRWorkPassRenewals: (params) => api.get('/confidential/work-pass/decisions/renewals/', { params }),
  updateHRWorkPassRenewal: (id, data) => api.post(`/confidential/work-pass/decisions/${id}/update-renewal/`, data),
  getHRTerminationRequests: (params) => api.get('/confidential/work-pass/termination-requests/', { params }),
  getHRTerminationRequest: (id) => api.get(`/confidential/work-pass/termination-requests/${id}/`),
  approveHRTerminationRequest: (id, data) => api.post(`/confidential/work-pass/termination-requests/${id}/approve/`, data),
  rejectHRTerminationRequest: (id, data) => api.post(`/confidential/work-pass/termination-requests/${id}/reject/`, data),
  recordTerminationWitnessedException: (id, data) => api.post(`/confidential/work-pass/termination-requests/${id}/witnessed-exception/`, data),
  getHRProfilesConsentPending: () => api.get('/confidential/hr-profiles/consent_pending/'),
  
  // PDPA Consent
  acceptConsent: () => api.post('/confidential/hr-profiles/accept_consent/'),
  
  // CPF Rates (calculated based on employee profile)
  getCPFRates: (hrProfileId) => api.get(`/confidential/hr-profiles/${hrProfileId}/cpf_rates/`),

  // Foreign Worker Levy info (for WP and S Pass holders)
  getLevyInfo: (hrProfileId) => api.get(`/confidential/hr-profiles/${hrProfileId}/levy_info/`),
  
  // Emergency Contacts
  getEmergencyContacts: (params) => api.get('/confidential/emergency-contacts/', { params }),
  createEmergencyContact: (data) => api.post('/confidential/emergency-contacts/', data),
  updateEmergencyContact: (id, data) => api.patch(`/confidential/emergency-contacts/${id}/`, data),
  deleteEmergencyContact: (id) => api.delete(`/confidential/emergency-contacts/${id}/`),
  getEmergencyContactsConsentPending: () => api.get('/confidential/emergency-contacts/consent_pending/'),
  getEmergencyContactStatistics: () => api.get('/confidential/emergency-contacts/statistics/'),
  exportEmergencyContacts: () => api.get('/confidential/emergency-contacts/export/'),
  
  // Staff Allowances
  getStaffAllowances: (params) => api.get('/confidential/allowances/', { params }),
  createStaffAllowance: (data) => api.post('/confidential/allowances/', data),
  updateStaffAllowance: (id, data) => api.patch(`/confidential/allowances/${id}/`, data),
  deleteStaffAllowance: (id) => api.delete(`/confidential/allowances/${id}/`),
  getActiveAllowances: () => api.get('/confidential/allowances/active_allowances/'),
  getAllowancesPendingApproval: () => api.get('/confidential/allowances/pending_approval/'),
  getAllowanceStatistics: () => api.get('/confidential/allowances/statistics/'),
  
  // Payroll Deductions (Recurring & Ad-Hoc)
  getPayrollDeductions: (params) => api.get('/confidential/payroll-deductions/', { params }),
  getPayrollDeduction: (id) => api.get(`/confidential/payroll-deductions/${id}/`),
  createPayrollDeduction: (data) => api.post('/confidential/payroll-deductions/', data),
  updatePayrollDeduction: (id, data) => api.patch(`/confidential/payroll-deductions/${id}/`, data),
  deletePayrollDeduction: (id) => api.delete(`/confidential/payroll-deductions/${id}/`),
  getActivePayrollDeductions: () => api.get('/confidential/payroll-deductions/active/'),
  getPayrollDeductionsForPeriod: (params) => api.get('/confidential/payroll-deductions/for_period/', { params }),

  // Employee Dependents (Extended Childcare Leave)
  getEmployeeDependents: (params) => api.get('/confidential/employee-dependents/', { params }),
  getMyDependents: () => api.get('/confidential/employee-dependents/my_dependents/'),
  createEmployeeDependent: (data) => api.post('/confidential/employee-dependents/', data),
  updateEmployeeDependent: (id, data) => api.patch(`/confidential/employee-dependents/${id}/`, data),
  deleteEmployeeDependent: (id) => api.delete(`/confidential/employee-dependents/${id}/`),
  checkChildcareEligibility: (id) => api.post(`/confidential/employee-dependents/${id}/check_eligibility/`),
  getEligibleChildren: () => api.get('/confidential/employee-dependents/eligible_children/'),
  getDependentStatistics: () => api.get('/confidential/employee-dependents/statistics/'),
  
  // Extended Childcare Leave Eligibility
  getExtendedChildcareEligibility: (params) => api.get('/confidential/extended-childcare-eligibility/', { params }),
  getMyChildcareEligibility: () => api.get('/confidential/extended-childcare-eligibility/my_eligibility/'),
  createChildcareEligibility: (data) => api.post('/confidential/extended-childcare-eligibility/', data),
  updateChildcareEligibility: (id, data) => api.patch(`/confidential/extended-childcare-eligibility/${id}/`, data),
  recalculateEligibility: (id) => api.post(`/confidential/extended-childcare-eligibility/${id}/recalculate/`),
  recordLeaveUsage: (id, data) => api.post(`/confidential/extended-childcare-eligibility/${id}/record_leave/`),
  getEligibilityStatistics: () => api.get('/confidential/extended-childcare-eligibility/statistics/'),
  recalculateAllEligibilities: () => api.post('/confidential/extended-childcare-eligibility/recalculate_all/'),
  
  // Driving Licenses
  getDrivingLicenses: (staffId) => api.get(`/confidential/driving-licenses/?staff_id=${staffId}`),
  getMyDrivingLicenses: () => api.get('/confidential/driving-licenses/my_licenses/'),
  createDrivingLicense: (data) => api.post('/confidential/driving-licenses/', data),
  updateDrivingLicense: (id, data) => api.patch(`/confidential/driving-licenses/${id}/`, data),
  deleteDrivingLicense: (id) => api.delete(`/confidential/driving-licenses/${id}/`),
  getDrivingLicenseStatistics: () => api.get('/confidential/driving-licenses/statistics/'),
  getExpiringLicenses: (days = 90) => api.get(`/confidential/driving-licenses/expiring/?days=${days}`),
  
  // Staff Payroll
  getEmployeeSalary: (params) => api.get('/confidential/payroll/', { params }),
  createEmployeeSalary: (data) => api.post('/confidential/payroll/', data),
  updateEmployeeSalary: (id, data) => api.patch(`/confidential/payroll/${id}/`, data),
  deleteEmployeeSalary: (id) => api.delete(`/confidential/payroll/${id}/`),
  getCurrentPayroll: () => api.get('/confidential/payroll/current_payroll/'),
  getSalaryStatistics: () => api.get('/confidential/payroll/salary_statistics/'),
  
  // Data Processing Records (PDPA Compliance)
  getDataProcessingRecords: (params) => api.get('/confidential/data-processing/', { params }),
  createDataProcessingRecord: (data) => api.post('/confidential/data-processing/', data),
  getComplianceReport: () => api.get('/confidential/data-processing/compliance_report/'),
  getDataExports: () => api.get('/confidential/data-processing/data_exports/'),
  
  // Documents (placeholder - ViewSet not implemented yet)
  getDocuments: (params) => api.get('/confidential/documents/', { params }),
  createDocument: (data) => api.post('/confidential/documents/', data),
  updateDocument: (id, data) => api.patch(`/confidential/documents/${id}/`, data),
  deleteDocument: (id) => api.delete(`/confidential/documents/${id}/`),
  downloadDocument: (id) => api.get(`/confidential/documents/${id}/download/`, { responseType: 'blob' }),
  
  // Document Approvals (employee-scope)
  getMyDocumentApprovals: async () => {
    // Try confidential employee-scoped endpoint first, then public-scoped variant
    const tryEndpoints = [
      '/confidential/document-approvals/my/',
      '/document-approvals/my/',
      '/confidential/document-approvals/?me=true',
      '/document-approvals/?me=true'
    ];
    let lastError;
    for (const ep of tryEndpoints) {
      try {
        return await api.get(ep);
      } catch (e) {
        lastError = e;
        if (e?.response?.status !== 404) break;
      }
    }
    throw lastError;
  },
  downloadDocumentApproval: (id) =>
    api.get(`/confidential/documents/${id}/download/`, { responseType: 'blob' }),
  uploadOtherDocument: (file, targetStaffId = null) => {
    const formData = new FormData();
    formData.append('file', file);
    if (targetStaffId) {
      formData.append('target_staff_id', targetStaffId);
    }
    return api.post('/other-document/upload/', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },

  // Certificate lists (employee)
  getChildCertificates: () => api.get('/child-certificate/list/'),
  getMarriageCertificates: () => api.get('/marriage-certificate/list/'),
  getIdCards: () => api.get('/id-card/list/'),
  getAllDocumentApprovals: (params) => api.get('/document-approvals/all/', { params }),
  deleteChildCertificate: (id) => api.delete(`/child-certificate/${id}/`),
  deleteMarriageCertificate: (id) => api.delete(`/marriage-certificate/${id}/`),
  
  // NS Call-up Order document upload
  uploadNSDocument: (file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post('/ns-document/upload/', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },
  deleteNSDocumentApproval: (documentApprovalId) => {
    return api.delete(`/ns-document/${documentApprovalId}/delete/`);
  },
  // Death Certificate document upload (Compassionate Leave)
  uploadDeathCertificate: (file, leaveApplicationId = null) => {
    const formData = new FormData();
    formData.append('file', file);
    if (leaveApplicationId) {
      formData.append('leave_application_id', leaveApplicationId);
    }
    return api.post('/death-certificate/upload/', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },
  deleteDeathCertificate: (documentApprovalId) => {
    return api.delete(`/death-certificate/${documentApprovalId}/delete/`);
  },
  // NS Call-up Order verification
  getNSVerificationData: (documentApprovalId) => {
    return api.get(`/ns-call-up/verification/${documentApprovalId}/`);
  },
  getNSVerificationDataByLeave: (leaveApplicationId) => {
    return api.get(`/ns-call-up/verification/leave/${leaveApplicationId}/`);
  },
  verifyNSCallUp: (payload) => {
    return api.post('/ns-call-up/verify/', payload);
  },
  // Preview helper: build URL like /documents/view/<path>/
  // Django URL pattern requires trailing slash: path('documents/view/<path:file_path>/', ...)
  buildDocumentViewUrl: (filePath) => {
    // Remove any trailing slashes from filePath to prevent double slashes, then add exactly one
    const cleanPath = filePath ? filePath.replace(/\/+$/, '') : '';
    return `/documents/view/${cleanPath}/`;
  },
  // Fetch a document as a blob via axios (sends JWT Authorization header).
  // Use this for <iframe>/<img> previews instead of raw URLs, which bypass auth.
  viewDocumentBlob: (filePath) => {
    const cleanPath = filePath ? filePath.replace(/\/+$/, '') : '';
    return api.get(`/documents/view/${cleanPath}/`, { responseType: 'blob' });
  },
  
  // Birth and Marriage Certificates
  deleteChildCertificate: (id) => api.delete(`/child-certificate/${id}/`),
  deleteMarriageCertificate: (id) => api.delete(`/marriage-certificate/${id}/`),
  
  // Qualification Certificates (Education & Professional)
  uploadEducationCertificate: (file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post('/education-certificate/upload/', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },
  uploadProfessionalCertificate: (file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post('/professional-certificate/upload/', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },
  getQualificationVerificationData: (documentApprovalId) => {
    return api.get(`/qualification/verification/${documentApprovalId}/`);
  },
  editQualificationOCR: (payload) => {
    return api.post('/qualification/edit-ocr/', payload);
  },
  submitQualification: (payload) => {
    return api.post('/qualification/submit/', payload);
  },
  verifyQualification: (payload) => {
    return api.post('/qualification/verify/', payload);
  },
  deleteQualificationDocument: (documentApprovalId) => {
    return api.delete(`/qualification/${documentApprovalId}/`);
  },
  /** HR-only: delete verified/pending employee document (DA and/or profile credential). */
  hrDeleteEmployeeDocument: (payload) => {
    return api.post('/hr/documents/delete/', payload);
  },

  identifyCertificateScheme: (payload) => {
    return api.post('/hr/certificates/identify-scheme/', payload);
  },
  
  // Document Requests - MOVED to confidential namespace for security
  getDocumentRequests: (params = {}) => api.get('/confidential/document-requests/', { params }),
  getMyDocumentRequests: () => api.get('/confidential/document-requests/my_requests/'),
  createDocumentRequest: (data) => api.post('/confidential/document-requests/', data),
  approveDocumentRequest: (id) => api.post(`/confidential/document-requests/${id}/approve/`),
  rejectDocumentRequest: (id, reason='') => api.post(`/confidential/document-requests/${id}/reject/`, { reason }),
  cancelDocumentRequest: (id, data) => api.post(`/confidential/document-requests/${id}/cancel/`, data),
  retryDocumentRequestGeneration: (id) => api.post(`/confidential/document-requests/${id}/retry-generation/`),
  previewDocumentRequestPdf: (id) => api.get(`/confidential/document-requests/${id}/preview_pdf/`, { responseType: 'blob' }),
  
  // HR Letters
  getHRLetters: (params = {}) => api.get('/confidential/hr-letters/', { params }),
  downloadHRLetter: (id) => api.get(`/confidential/hr-letters/${id}/download/`, { responseType: 'blob' }),
  regenerateHRLetter: (id) => api.post(`/confidential/hr-letters/${id}/regenerate/`),
  previewHRLetter: (id) => api.get(`/confidential/hr-letters/${id}/preview/`, { responseType: 'blob' }),

  // Asset Acknowledgement Letters (generated when employee acknowledges asset receipt)
  getAssetAcknowledgementLetters: (params) => api.get('/confidential/asset-ack-letters/', { params }),
  getAssetAcknowledgementLetter: (id) => api.get(`/confidential/asset-ack-letters/${id}/`),
  downloadAssetAcknowledgementLetter: (id) => api.get(`/confidential/asset-ack-letters/${id}/download/`, { responseType: 'blob' }),
  previewAssetAcknowledgementLetter: (id) => api.get(`/confidential/asset-ack-letters/${id}/preview/`, { responseType: 'blob' }),
  resendAssetAcknowledgementLetterEmail: (id) => api.post(`/confidential/asset-ack-letters/${id}/resend-email/`),

  // Offboarding Workflow Management
  getOffboardingWorkflows: () => api.get('/confidential/offboarding/'),
  createOffboardingWorkflow: (data) => api.post('/confidential/offboarding/create/', data),
  getOffboardingWorkflow: (id) => api.get(`/confidential/offboarding/${id}/`),
  updateOffboardingWorkflow: (id, data) => api.patch(`/confidential/offboarding/${id}/`, data),
  updateOffboardingStep: (id, stepNumber, data) => api.post(`/confidential/offboarding/${id}/step-${stepNumber}/`, data),
  // Explicit withdrawal action (backend should set workflow_status to 'withdrawn')
  withdrawOffboardingWorkflow: (id, data) => api.post(`/confidential/offboarding/${id}/withdraw/`, data),
  getOffboardingHistory: (id) => api.get(`/confidential/offboarding/${id}/history/`),
  downloadResignationLetter: (id) => api.get(`/confidential/offboarding/${id}/download-resignation-letter/`, { responseType: 'blob' }),

  // Offboarding case engine
  getOffboardingCases: (params = {}) => api.get('/confidential/offboarding/cases/', { params }),
  getOffboardingCase: (id) => api.get(`/confidential/offboarding/cases/${id}/`),
  getMyOffboardingCase: () => api.get('/confidential/offboarding/cases/mine/'),
  getAssignedOffboardingTasks: () => api.get('/confidential/offboarding/cases/assigned/'),
  releaseOffboardingPlan: (id) => api.post(`/confidential/offboarding/cases/${id}/release/`),
  setOffboardingDates: (id, data) => api.post(`/confidential/offboarding/cases/${id}/dates/`, data),
  recordFullLeaveEncashment: (id) => api.post(`/confidential/offboarding/cases/${id}/leave/encashment/`),
  recordOffboardingDeparture: (id, data) => api.post(`/confidential/offboarding/cases/${id}/departure/`, data),
  confirmOffboardingDeparture: (id, data) => api.post(`/confidential/offboarding/cases/${id}/departure/confirm/`, data),
  recordOffboardingWorkPassCancellation: (id, data) => api.post(`/confidential/offboarding/cases/${id}/work-pass-cancellation/`, data),
  recordOffboardingIR21Filing: (id, data) => api.post(`/confidential/offboarding/cases/${id}/ir21/filing/`, data),
  recordOffboardingIR21Exemption: (id, data) => api.post(`/confidential/offboarding/cases/${id}/ir21/exemption/`, data),
  recordOffboardingIR21Directive: (id, data) => api.post(`/confidential/offboarding/cases/${id}/ir21/directive/`, data),
  uploadOffboardingIR21DirectiveDocument: (id, formData) => api.post(
    `/confidential/offboarding/cases/${id}/ir21/directive/upload/`,
    formData,
    { headers: { 'Content-Type': 'multipart/form-data' } },
  ),
  confirmOffboardingIR21DirectiveFromDocument: (id, data) => api.post(
    `/confidential/offboarding/cases/${id}/ir21/directive/confirm/`,
    data,
  ),
  downloadOffboardingIR21DirectiveDocument: (id, documentId) => api.get(
    `/confidential/offboarding/cases/${id}/ir21/directive/documents/${documentId}/`,
    { responseType: 'blob' },
  ),
  recordOffboardingIR21Payment: (id, data) => api.post(`/confidential/offboarding/cases/${id}/ir21/payment/`, data),
  downloadOffboardingFlightTicket: (id) => api.get(
    `/confidential/offboarding/cases/${id}/flight-ticket/`,
    { responseType: 'blob' },
  ),
  completeOffboardingTask: (id, taskKey, data = {}) => api.post(`/confidential/offboarding/cases/${id}/tasks/${taskKey}/complete/`, data),
  overrideOffboardingTask: (id, taskKey, data) => api.post(`/confidential/offboarding/cases/${id}/tasks/${taskKey}/override/`, data),
  requestOffboardingWaiver: (id, taskKey, data) => api.post(`/confidential/offboarding/cases/${id}/tasks/${taskKey}/waiver/`, data),
  decideOffboardingWaiver: (waiverId, data) => api.post(`/confidential/offboarding/cases/waivers/${waiverId}/decide/`, data),
  emergencyOffboardingCutoff: (id, data) => api.post(`/confidential/offboarding/cases/${id}/emergency-cutoff/`, data),
  closeOffboardingCase: (id) => api.post(`/confidential/offboarding/cases/${id}/close/`),
  previewLeaveOffset: (id, data) => api.post(`/confidential/offboarding/cases/${id}/offset/preview/`, data),
  proposeLeaveOffset: (id, data) => api.post(`/confidential/offboarding/cases/${id}/offset/propose/`, data),
  recordAgreedLeaveOffset: (id, data) => api.post(`/confidential/offboarding/cases/${id}/offset/record/`, data),
  decideLeaveOffset: (requestId, data) => api.post(`/confidential/offboarding/cases/offset/${requestId}/decide/`, data),
  processLeaveOffset: (requestId, data) => api.post(`/confidential/offboarding/cases/offset/${requestId}/process/`, data),
};

// Configuration API
export const configAPI = {
  // Employee Handbook (read)
  getHandbook: () => api.get('/configuration/handbook/current/'),

  // Employee Handbook CMS (admin)
  getHandbookSections: (params = {}) => api.get('/configuration/handbook/sections/', { params }),
  getHandbookSectionRevisions: (sectionId) => api.get(`/configuration/handbook/sections/${sectionId}/revisions/`),
  createHandbookDraft: (sectionId) => api.post(`/configuration/handbook/sections/${sectionId}/create_draft/`),
  updateHandbookSection: (sectionId, data) => api.patch(`/configuration/handbook/sections/${sectionId}/`, data),
  getHandbookRevision: (revisionId) => api.get(`/configuration/handbook/revisions/${revisionId}/`),
  updateHandbookRevision: (revisionId, data) => api.patch(`/configuration/handbook/revisions/${revisionId}/`, data),
  submitHandbookRevision: (revisionId) => api.post(`/configuration/handbook/revisions/${revisionId}/submit_for_review/`),
  approveHandbookRevision: (revisionId) => api.post(`/configuration/handbook/revisions/${revisionId}/approve/`),
  publishHandbookRevision: (revisionId) => api.post(`/configuration/handbook/revisions/${revisionId}/publish/`),

  // Employee Handbook editions (formal releases)
  getHandbookEditions: () => api.get('/configuration/handbook/editions/'),
  createHandbookEdition: (data) => api.post('/configuration/handbook/editions/', data),
  updateHandbookEdition: (editionId, data) => api.patch(`/configuration/handbook/editions/${editionId}/`, data),
  publishHandbookEdition: (editionId) => api.post(`/configuration/handbook/editions/${editionId}/publish/`),

  // Employee Handbook acknowledgements
  getHandbookAcknowledgement: () => api.get('/configuration/handbook/acknowledgement/me/'),
  acknowledgeHandbook: () => api.post('/configuration/handbook/acknowledgement/me/'),
  getHandbookAckDashboard: () => api.get('/configuration/handbook/acknowledgement/dashboard/'),

  // Company Policies (read + acknowledgement)
  getPolicies: () => api.get('/configuration/policies/'),
  getPolicy: (policyKey) => api.get(`/configuration/policies/${policyKey}/`),
  acknowledgePolicy: (policyKey) => api.post(`/configuration/policies/${policyKey}/acknowledge/`),
  getPolicySignedPdf: (policyKey) => api.get(`/configuration/policies/${policyKey}/pdf/`, { responseType: 'blob' }),
  getPolicyAckDashboard: () => api.get('/configuration/policies/acknowledgement/dashboard/'),

  // Company Policies CMS (admin)
  getPolicyDocuments: (params = {}) => api.get('/configuration/policies/documents/', { params }),
  getPolicyDocumentRevisions: (policyId) => api.get(`/configuration/policies/documents/${policyId}/revisions/`),
  createPolicyDraft: (policyId) => api.post(`/configuration/policies/documents/${policyId}/create_draft/`),
  updatePolicyDocument: (policyId, data) => api.patch(`/configuration/policies/documents/${policyId}/`, data),
  getPolicyRevision: (revisionId) => api.get(`/configuration/policies/revisions/${revisionId}/`),
  updatePolicyRevision: (revisionId, data) => api.patch(`/configuration/policies/revisions/${revisionId}/`, data),
  submitPolicyRevision: (revisionId) => api.post(`/configuration/policies/revisions/${revisionId}/submit_for_review/`),
  approvePolicyRevision: (revisionId) => api.post(`/configuration/policies/revisions/${revisionId}/approve/`),
  publishPolicyRevision: (revisionId) => api.post(`/configuration/policies/revisions/${revisionId}/publish/`),

  // Training Materials (authenticated employee reference library)
  getTrainingMaterials: () => api.get('/configuration/training-materials/'),
  getTrainingMaterial: (materialKey) => api.get(`/configuration/training-materials/${materialKey}/`),
  getTrainingMaterialPdf: (materialKey) => api.get(
    `/configuration/training-materials/${materialKey}/pdf/`,
    { responseType: 'blob' },
  ),

  // Singapore bank catalog (legal name → BIC + MAS bank code; reference data)
  getSingaporeBanks: () => api.get('/singapore-banks/'),

  // Departments
  getDepartments: () => api.get('/configuration/departments/'),
  createDepartment: (data) => api.post('/configuration/departments/', data),
  updateDepartment: (id, data) => api.patch(`/configuration/departments/${id}/`, data),
  deleteDepartment: (id) => api.delete(`/configuration/departments/${id}/`),
  
  // Locations  
  getLocations: () => api.get('/configuration/locations/'),
  createLocation: (data) => api.post('/configuration/locations/', data),
  updateLocation: (id, data) => api.patch(`/configuration/locations/${id}/`, data),
  deleteLocation: (id) => api.delete(`/configuration/locations/${id}/`),
  
  // Classifications
  getClassifications: () => api.get('/configuration/classifications/'),
  createClassification: (data) => api.post('/configuration/classifications/', data),
  updateClassification: (id, data) => api.patch(`/configuration/classifications/${id}/`, data),
  deleteClassification: (id) => api.delete(`/configuration/classifications/${id}/`),
  
  // Construction Worker Classifications
  getConstructionWorkerClassifications: (params) => api.get('/configuration/construction-worker-classifications/', { params }),
  getConstructionWorkerClassification: (id) => api.get(`/configuration/construction-worker-classifications/${id}/`),
  createConstructionWorkerClassification: (data) => api.post('/configuration/construction-worker-classifications/', data),
  deleteConstructionWorkerClassification: (id) => api.delete(`/configuration/construction-worker-classifications/${id}/`),
  
  // Construction Worker Classification Actions
  checkUpgradeEligibility: (id) => api.post(`/configuration/construction-worker-classifications/${id}/check_upgrade_eligibility/`),
  getMissingCertifications: (id) => api.get(`/configuration/construction-worker-classifications/${id}/missing_certifications/`),
  getCertificateEvidence: (staffId) => api.get('/configuration/construction-worker-classifications/certificate-evidence/', {
    params: { employee_staff_id: staffId },
  }),
  getWorkerR1Registrations: (staffId) => api.get(`/configuration/construction-workers/${staffId}/r1-registrations/`),
  getWorkerConstructionStatus: (staffId) => api.get(`/configuration/construction-workers/${staffId}/construction-status/`),
  getConstructionLevyOptimization: (params) => api.get('/configuration/construction-workers/levy-optimization/', { params }),
  
  // Construction Worker Upgrade Pathways
  getConstructionWorkerUpgradePathways: (params) => api.get('/configuration/construction-worker-upgrade-pathways/', { params }),
  getConstructionWorkerUpgradePathway: (id) => api.get(`/configuration/construction-worker-upgrade-pathways/${id}/`),
  
  // Certification Requirements
  getCertificationRequirements: (params) => api.get('/configuration/certification-requirements/', { params }),
  getCertificationRequirementsByPathway: (params) => api.get('/configuration/certification-requirements/requirements_by_pathway/', { params }),
  
  // Designations
  getDesignations: () => api.get('/configuration/designations/'),
  createDesignation: (data) => api.post('/configuration/designations/', data),
  updateDesignation: (id, data) => api.patch(`/configuration/designations/${id}/`, data),
  deleteDesignation: (id) => api.delete(`/configuration/designations/${id}/`),
  
  // Education
  getEducation: (params) => api.get('/education/', { params }),
  createEducation: (data) => api.post('/education/', data),
  updateEducation: (id, data) => api.patch(`/education/${id}/`, data),
  deleteEducation: (id) => api.delete(`/education/${id}/`),
  getEducationVerificationPending: () => api.get('/education/verification_pending/'),
  
  // Education Institutions
  getEducationInstitutions: (params) => api.get('/configuration/education-institutions/', { params }),
  getEducationInstitution: (id) => api.get(`/configuration/education-institutions/${id}/`),
  createEducationInstitution: (data) => api.post('/configuration/education-institutions/', data),
  updateEducationInstitution: (id, data) => api.patch(`/configuration/education-institutions/${id}/`, data),
  deleteEducationInstitution: (id) => api.delete(`/configuration/education-institutions/${id}/`),
  getEducationInstitutionsAutocomplete: (params) => api.get('/configuration/education-institutions/autocomplete/', { params }),
  syncEducationInstitutions: () => api.post('/configuration/education-institutions/sync_hipo/'),
  
  // Institution Types
  getInstitutionTypes: () => api.get('/configuration/institution-types/'),
  createInstitutionType: (data) => api.post('/configuration/institution-types/', data),
  updateInstitutionType: (id, data) => api.patch(`/configuration/institution-types/${id}/`, data),
  deleteInstitutionType: (id) => api.delete(`/configuration/institution-types/${id}/`),
  
  // Trade Categories
  getTradeCategories: () => api.get('/configuration/trade-categories/'),

  // MOM occupation titles (optionally filtered by pass type: 'wp' | 's_pass')
  getMOMDesignations: (params = {}) => api.get('/configuration/mom-designations/', { params }),

  // Employment Types
  getEmploymentTypes: () => api.get('/configuration/employment-types/'),
  getPayGrades: () => api.get('/configuration/pay-grades/'),
  
  // Leave Types
  getLeaveTypes: () => api.get('/configuration/leave-types/'),
  getPublicHolidays: () => api.get('/configuration/public-holidays/'),
  
  // Allowance Types (moved to configuration app)
  getAllowanceTypes: () => api.get('/configuration/allowance-types/'),
  
  // Overtime Policies
  getOvertimePolicies: () => api.get('/configuration/overtime-policies/'),
  
  // Payroll Configuration
  getCPFRates: () => api.get('/configuration/cpf-contribution-rates/'),
  getWorkweekPolicies: () => api.get('/configuration/workweek-policies/'),
  
  // IRAS Tax Configuration
  getIRASTaxBrackets: (taxYear) => api.get('/configuration/iras-tax-brackets/', { params: taxYear ? { tax_year: taxYear } : {} }),
  createIRASTaxBracket: (data) => api.post('/configuration/iras-tax-brackets/', data),
  updateIRASTaxBracket: (id, data) => api.patch(`/configuration/iras-tax-brackets/${id}/`, data),
  deleteIRASTaxBracket: (id) => api.delete(`/configuration/iras-tax-brackets/${id}/`),
  validateBracketContinuity: (taxYear, effectiveFrom) => api.get('/configuration/iras-tax-brackets/validate-continuity/', { params: { tax_year: taxYear, effective_from: effectiveFrom } }),
  getCurrentTaxBrackets: (taxYear) => api.get('/configuration/iras-tax-brackets/current/', { params: { tax_year: taxYear } }),
  
  getIRASTaxReliefs: (taxYear) => api.get('/configuration/iras-tax-reliefs/', { params: taxYear ? { tax_year: taxYear } : {} }),
  createIRASTaxRelief: (data) => api.post('/configuration/iras-tax-reliefs/', data),
  updateIRASTaxRelief: (id, data) => api.patch(`/configuration/iras-tax-reliefs/${id}/`, data),
  deleteIRASTaxRelief: (id) => api.delete(`/configuration/iras-tax-reliefs/${id}/`),
  getCurrentTaxReliefs: (taxYear) => api.get('/configuration/iras-tax-reliefs/current/', { params: { tax_year: taxYear } }),
  
  // SDL Policy Configuration
  getSdlPolicies: () => api.get('/configuration/sdl-policies/'),
  getCurrentSdlPolicy: () => api.get('/configuration/sdl-policies/current/'),
  createSdlPolicy: (data) => api.post('/configuration/sdl-policies/', data),
  updateSdlPolicy: (id, data) => api.patch(`/configuration/sdl-policies/${id}/`, data),
  deleteSdlPolicy: (id) => api.delete(`/configuration/sdl-policies/${id}/`),
  duplicateSdlPolicy: (id, effectiveFrom) => api.post(`/configuration/sdl-policies/${id}/duplicate/`, { effective_from: effectiveFrom }),
  analyzeSdlPolicyImpact: (data) => api.post('/configuration/sdl-policies/analyze_impact/', data),
  
  // Leave Approval Workflows (Versioned)
  getLeaveApprovalWorkflows: (params = {}) => api.get('/configuration/leave-approval-workflows/', { params }),
  getLeaveApprovalWorkflow: (id) => api.get(`/configuration/leave-approval-workflows/${id}/`),
  createLeaveApprovalWorkflow: (data) => api.post('/configuration/leave-approval-workflows/', data),
  updateLeaveApprovalWorkflow: (id, data) => api.patch(`/configuration/leave-approval-workflows/${id}/`, data),
  deleteLeaveApprovalWorkflow: (id) => api.delete(`/configuration/leave-approval-workflows/${id}/`),
  getActiveWorkflows: () => api.get('/configuration/leave-approval-workflows/active_workflows/'),
  getWorkflowsByClassification: (classification) => api.get('/configuration/leave-approval-workflows/by_classification/', { params: { classification } }),
  getWorkflowVersionHistory: (id) => api.get(`/configuration/leave-approval-workflows/${id}/version_history/`),
  duplicateWorkflow: (id, data) => api.post(`/configuration/leave-approval-workflows/${id}/duplicate/`, data),
  previewWorkflowForEmployee: (staffId) => api.post('/configuration/leave-approval-workflows/preview_for_employee/', { staff_id: staffId }),
  getWorkflowSummary: () => api.get('/configuration/leave-approval-workflows/summary/'),

  // Workflow Assignments (maps workflows to org targets)
  getWorkflowAssignments: (params = {}) => api.get('/configuration/workflow-assignments/', { params }),
  getWorkflowAssignment: (id) => api.get(`/configuration/workflow-assignments/${id}/`),
  createWorkflowAssignment: (data) => api.post('/configuration/workflow-assignments/', data),
  updateWorkflowAssignment: (id, data) => api.patch(`/configuration/workflow-assignments/${id}/`, data),
  deleteWorkflowAssignment: (id) => api.delete(`/configuration/workflow-assignments/${id}/`),
  getAssignmentsByDepartment: (departmentId) => api.get('/configuration/workflow-assignments/by_department/', { params: { department_id: departmentId } }),
  bulkPreviewAssignments: (staffIds) => api.post('/configuration/workflow-assignments/bulk_preview/', { staff_ids: staffIds }),

  // Training Enrollments
  getTrainingEnrollments: (params) => api.get('/configuration/employee-training-enrollments/', { params }),

  // Safety Certification Types (catalog)
  getSafetyCertificationTypes: (params = {}) => api.get('/configuration/safety-certification-types/', { params }),

  // Mandatory Certification Rules (regulatory compliance engine)
  getMandatoryCertRules: (params = {}) => api.get('/configuration/course-library/mandatory-cert-rules/', { params }),
  createMandatoryCertRule: (data) => api.post('/configuration/course-library/mandatory-cert-rules/', data),
  updateMandatoryCertRule: (id, data) => api.patch(`/configuration/course-library/mandatory-cert-rules/${id}/`, data),
  deleteMandatoryCertRule: (id) => api.delete(`/configuration/course-library/mandatory-cert-rules/${id}/`),
  getMandatoryCertComplianceDashboard: () => api.get('/configuration/course-library/mandatory-cert-rules/compliance_dashboard/'),

  // System Settings
  getSystemSettings: () => api.get('/configuration/system-settings/'),
  getIntegrationKeys: () => api.get('/configuration/integration-keys/'),
  
  // Generic CRUD operations for configuration
  create: (endpoint, data) => api.post(`/configuration/${endpoint}/`, data),
  update: (endpoint, id, data) => api.patch(`/configuration/${endpoint}/${id}/`, data),
  deleteConfigItem: (endpoint, id) => api.delete(`/configuration/${endpoint}/${id}/`),
};

// Utility functions for common operations
export const apiUtils = {
  // Export data to CSV
  exportToCSV: async (endpoint, params = {}, filename = 'export') => {
    try {
      const response = await api.get(endpoint, { 
        params: { ...params, export: 'csv' },
        responseType: 'blob'
      });
      
      const blob = new Blob([response.data], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.style.display = 'none';
      a.href = url;
      a.download = `${filename}_${getTodayInSingaporeTimezone()}.csv`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      
      return true;
    } catch (error) {
      console.error('Export failed:', error);
      return false;
    }
  },
  
  // Batch operations
  batchCreate: (endpoint, dataArray) => {
    return Promise.all(dataArray.map(data => api.post(endpoint, data)));
  },
  
  batchUpdate: (endpoint, updates) => {
    return Promise.all(updates.map(({ id, data }) => api.patch(`${endpoint}/${id}/`, data)));
  },
  
  batchDelete: (endpoint, ids) => {
    return Promise.all(ids.map(id => api.delete(`${endpoint}/${id}/`)));
  },
  
  // File upload helper
  uploadFile: (endpoint, file, additionalData = {}) => {
    const formData = new FormData();
    formData.append('file', file);
    
    Object.keys(additionalData).forEach(key => {
      formData.append(key, additionalData[key]);
    });
    
    return api.post(endpoint, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
  },
  
  // Pagination helper
  getAllPages: async (endpoint, params = {}) => {
    let allData = [];
    let nextUrl = endpoint;
    
    while (nextUrl) {
      const response = await api.get(nextUrl, { params });
      allData = [...allData, ...(response.data.results || [])];
      nextUrl = response.data.next;
      params = {}; // Clear params for subsequent requests
    }
    
    return allData;
  },
  
  // Employee Schedule Management
  getEmployeeSchedules: (params = {}) => api.get('/employee-schedules/', { params }),
  createEmployeeSchedule: (data) => api.post('/employee-schedules/', data),
  updateEmployeeSchedule: (id, data) => api.put(`/employee-schedules/${id}/`, data),
  deleteEmployeeSchedule: (id) => api.delete(`/employee-schedules/${id}/`),
  getCurrentScheduleAssignments: () => api.get('/employee-schedules/current_assignments/'),
  bulkAssignSchedules: (data) => api.post('/employee-schedules/bulk_assign/', data),
  getUnassignedEmployees: () => api.get('/employee-schedules/unassigned_employees/'),
  getDepartmentScheduleSummary: () => api.get('/employee-schedules/department_summary/'),
  extendScheduleAssignment: (id, data) => api.post(`/employee-schedules/${id}/extend_assignment/`, data),

  // Schedule Override Management (alternate Saturdays, ad-hoc overrides)
  getScheduleOverrides: (params = {}) => api.get('/schedule-overrides/', { params }),
  createScheduleOverride: (data) => api.post('/schedule-overrides/', data),
  updateScheduleOverride: (id, data) => api.patch(`/schedule-overrides/${id}/`, data),
  deleteScheduleOverride: (id) => api.delete(`/schedule-overrides/${id}/`),
  bulkCreateScheduleOverrides: (data) => api.post('/schedule-overrides/bulk_create/', data),
  bulkDeleteScheduleOverrides: (data) => api.post('/schedule-overrides/bulk_delete/', data),
  refreshScheduleOverrideAttendance: (id, data = {}) => api.post(`/schedule-overrides/${id}/refresh_attendance/`, data),
  refreshScheduleOverrideAttendanceByDate: (data) => api.post('/schedule-overrides/refresh_date/', data),
};

// Dormitory Management API
export const dormitoryAPI = {
  // Dormitory CRUD
  getDormitories: (params = {}) => api.get('/configuration/dormitories/', { params }),
  getDormitory: (id) => api.get(`/configuration/dormitories/${id}/`),
  createDormitory: (data) => api.post('/configuration/dormitories/', data),
  updateDormitory: (id, data) => api.put(`/configuration/dormitories/${id}/`, data),
  deleteDormitory: (id) => api.delete(`/configuration/dormitories/${id}/`),
  
  // Dormitory Actions
  activateDormitory: (id) => api.post(`/configuration/dormitories/${id}/activate/`),
  deactivateDormitory: (id) => api.post(`/configuration/dormitories/${id}/deactivate/`),
  getDormitorySummary: () => api.get('/configuration/dormitories/summary/'),
  getActiveDormitories: () => api.get('/configuration/dormitories/', { params: { is_active: true } }),
  
  // Worker Dormitory Assignments
  getAssignments: (params = {}) => api.get('/dormitory-assignments/', { params }),
  getAssignment: (id) => api.get(`/dormitory-assignments/${id}/`),
  createAssignment: (data) => api.post('/dormitory-assignments/', data),
  updateAssignment: (id, data) => api.put(`/dormitory-assignments/${id}/`, data),
  deleteAssignment: (id) => api.delete(`/dormitory-assignments/${id}/`),
  
  // Assignment Actions
  transferWorker: (assignmentId, data) => api.post(`/dormitory-assignments/${assignmentId}/transfer/`, data),
  completeAssignment: (assignmentId, data) => api.post(`/dormitory-assignments/${assignmentId}/complete/`, data),
  cancelAssignment: (assignmentId, data) => api.post(`/dormitory-assignments/${assignmentId}/cancel/`, data),
  
  // Assignment Queries
  getUnassignedWorkers: () => api.get('/dormitory-assignments/unassigned_workers/'),
  getAssignmentSummary: (params = {}) => api.get('/dormitory-assignments/summary/', { params }),
  getActiveAssignments: () => api.get('/dormitory-assignments/', { params: { status: 'active' } }),
  getAssignmentsByDormitory: (dormitoryId) => api.get('/dormitory-assignments/', { params: { dormitory: dormitoryId } }),
  getAssignmentsByWorker: (staffId) => api.get('/dormitory-assignments/', { params: { staff_user: staffId } }),

  // Dormitory Portal (live data — Dorm Portal is the source of truth for
  // locations, occupancy, and worker assignments). Powers all three tabs of
  // admin/dormitory-management.
  getDormitoryPortalOverview: (params = {}) =>
    api.get('/dormitory-portal/overview/', { params }),
  getDormitoryPortalResidents: (params = {}) =>
    api.get('/dormitory-portal/residents/', { params }),
};

// Asset Inventory Management API
export const assetInventoryAPI = {
  // Asset Categories
  getCategories: (params = {}) => api.get('/asset-inventory/categories/', { params }),
  getCategory: (id) => api.get(`/asset-inventory/categories/${id}/`),
  createCategory: (data) => api.post('/asset-inventory/categories/', data),
  updateCategory: (id, data) => api.patch(`/asset-inventory/categories/${id}/`, data),
  deleteCategory: (id) => api.delete(`/asset-inventory/categories/${id}/`),
  
  // Asset Types
  getAssetTypes: (params = {}) => api.get('/asset-inventory/types/', { params }),
  getAssetType: (id) => api.get(`/asset-inventory/types/${id}/`),
  createAssetType: (data) => api.post('/asset-inventory/types/', data),
  updateAssetType: (id, data) => api.patch(`/asset-inventory/types/${id}/`, data),
  deleteAssetType: (id) => api.delete(`/asset-inventory/types/${id}/`),
  
  // Assets
  getAssets: (params = {}) => api.get('/asset-inventory/assets/', { params }),
  getAsset: (id) => api.get(`/asset-inventory/assets/${id}/`),
  lookupByTag: (tag) => api.get('/asset-inventory/assets/lookup-by-tag/', { params: { tag } }),
  createAsset: (data) => api.post('/asset-inventory/assets/', data),
  updateAsset: (id, data) => api.patch(`/asset-inventory/assets/${id}/`, data),
  deleteAsset: (id) => api.delete(`/asset-inventory/assets/${id}/`),
  
  // Asset Export (bypasses pagination for PDF/XLSX/CSV generation)
  exportAssets: (params = {}) => api.get('/asset-inventory/assets/export/', { params }),

  // Asset Actions
  getAssetDashboard: () => api.get('/asset-inventory/assets/dashboard/'),
  getAvailableAssets: (params = {}) => api.get('/asset-inventory/assets/available/', { params }),
  getTechRefreshAssets: () => api.get('/asset-inventory/assets/tech_refresh/'),
  getExpiringWarranties: (days = 90) => api.get('/asset-inventory/assets/expiring_warranties/', { params: { days } }),
  getAssetHistory: (id) => api.get(`/asset-inventory/assets/${id}/history/`),

  // Endpoint Security (Trend Micro Apex One coverage + licensing)
  getEndpointSecuritySummary: () => api.get('/asset-inventory/assets/endpoint-security-summary/'),
  downloadEndpointSecurityReport: () => api.get('/asset-inventory/assets/endpoint-security-report/', { responseType: 'blob' }),
  assignAsset: (data) => api.post('/asset-inventory/assets/assign/', data),
  bulkAssignAssets: (data) => api.post('/asset-inventory/assets/bulk-assign/', data),
  transferAsset: (data) => api.post('/asset-inventory/assets/transfer/', data),
  returnToInventory: (data = {}) => {
    // Optional condition-at-receipt photos require multipart/form-data; the
    // JSON branch keeps the common (no-photo) path lightweight.
    if (data.photos && data.photos.length > 0) {
      const form = new FormData();
      Object.entries(data).forEach(([key, value]) => {
        if (key === 'photos') return;
        if (value === null || value === undefined || value === '') return;
        form.append(key, value);
      });
      data.photos.forEach((file) => form.append('photos', file));
      return api.post('/asset-inventory/assets/return_to_inventory/', form, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
    }
    const { photos, ...rest } = data;
    return api.post('/asset-inventory/assets/return_to_inventory/', rest);
  },
  markForDisposal: (id, data) => api.post(`/asset-inventory/assets/${id}/mark_for_disposal/`, data),
  returnToLessor: (id, data) => api.post(`/asset-inventory/assets/${id}/return-to-lessor/`, data),
  // Revoke an access-control credential (door access / facial recognition) in one
  // step. Server sets status=DEACTIVATED, records the deactivation date and
  // clears the assignee. Custodian/asset-admin only (enforced server-side).
  deactivateAccess: (id) => api.post(`/asset-inventory/assets/${id}/deactivate-access/`),
  
  // Asset Transfers (Audit Trail)
  getTransfers: (params = {}) => api.get('/asset-inventory/transfers/', { params }),
  getTransfer: (id) => api.get(`/asset-inventory/transfers/${id}/`),
  getRecentTransfers: (limit = 20) => api.get('/asset-inventory/transfers/recent/', { params: { limit } }),
  // Append supporting photos to an existing (completed) transfer.
  // Custodian/admin only (enforced server-side). Always multipart.
  addTransferPhotos: (transferId, photos = []) => {
    const form = new FormData();
    photos.forEach((file) => form.append('photos', file));
    return api.post(`/asset-inventory/transfers/${transferId}/photos/`, form, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  // Admin: re-fire the acknowledgement-letter Celery task for a single transfer
  // (or its parent batch). Used to recover from FAILED / stuck PENDING letters.
  regenerateAssetAcknowledgementLetter: (transferId) =>
    api.post(`/asset-inventory/transfers/${transferId}/regenerate-letter/`),
  // External holder: revoke any previous magic link and queue a fresh one.
  resendAssetReceiptLink: (transferId) =>
    api.post(`/asset-inventory/transfers/${transferId}/resend-receipt-link/`),
  
  // Checklists
  getChecklists: (params = {}) => api.get('/asset-inventory/checklists/', { params }),
  getChecklist: (id) => api.get(`/asset-inventory/checklists/${id}/`),
  getPendingChecklists: (params = {}) => api.get('/asset-inventory/checklists/pending/', { params }),
  createChecklist: (data) => api.post('/asset-inventory/checklists/create_checklist/', data),
  getChecklistSummary: (id) => api.get(`/asset-inventory/checklists/${id}/summary/`),
  getDepartmentPendingItems: (departmentId) => api.get('/asset-inventory/checklists/department_pending/', { params: { department_id: departmentId } }),
  getEmployeeAssetSummary: (employeeId) => api.get('/asset-inventory/checklists/employee_summary/', { params: { employee_id: employeeId } }),
  
  // Checklist Items
  getChecklistItems: (params = {}) => api.get('/asset-inventory/checklist-items/', { params }),
  updateChecklistItem: (id, data) => api.patch(`/asset-inventory/checklist-items/${id}/`, data),
  
  // Maintenance Logs
  getMaintenanceLogs: (params = {}) => api.get('/asset-inventory/maintenance/', { params }),
  createMaintenanceLog: (data) => api.post('/asset-inventory/maintenance/', data),
  updateMaintenanceLog: (id, data) => api.patch(`/asset-inventory/maintenance/${id}/`, data),
  deleteMaintenanceLog: (id) => api.delete(`/asset-inventory/maintenance/${id}/`),
  
  // Software Licenses
  getLicenses: (params = {}) => api.get('/asset-inventory/licenses/', { params }),
  getLicense: (id) => api.get(`/asset-inventory/licenses/${id}/`),
  createLicense: (data) => api.post('/asset-inventory/licenses/', data),
  updateLicense: (id, data) => api.patch(`/asset-inventory/licenses/${id}/`, data),
  deleteLicense: (id) => api.delete(`/asset-inventory/licenses/${id}/`),
  getExpiringLicenses: (days = 30) => api.get('/asset-inventory/licenses/expiring/', { params: { days } }),
  getLicenseSeats: (id) => api.get(`/asset-inventory/licenses/${id}/seat_assignments/`),
  assignLicenseSeat: (id, data) => api.post(`/asset-inventory/licenses/${id}/assign_seat/`, data),
  revokeLicenseSeat: (id, data) => api.post(`/asset-inventory/licenses/${id}/revoke_seat/`, data),

  // Fuel Cards
  getFuelCards: (params = {}) => api.get('/asset-inventory/fuel-cards/', { params }),
  getFuelCard: (id) => api.get(`/asset-inventory/fuel-cards/${id}/`),
  updateFuelCard: (id, data) => api.patch(`/asset-inventory/fuel-cards/${id}/`, data),

  // Diesel Tokens
  getDieselToken: (id) => api.get(`/asset-inventory/diesel-tokens/${id}/`),
  updateDieselToken: (id, data) => api.patch(`/asset-inventory/diesel-tokens/${id}/`, data),

  // Mobile lines (SIM subscriptions)
  updateMobileLine: (id, data) => api.patch(`/asset-inventory/mobile-lines/${id}/`, data),

  // Fleet Vehicles (used e.g. to pick a company vehicle to link a fuel card to)
  getFleetVehicles: (params = {}) => api.get('/asset-inventory/fleet-vehicles/', { params }),
  getFleetVehicle: (id) => api.get(`/asset-inventory/fleet-vehicles/${id}/`),
  updateFleetVehicle: (id, data) => api.patch(`/asset-inventory/fleet-vehicles/${id}/`, data),
  syncFleetVehicleCompliance: (id) =>
    api.post(`/asset-inventory/fleet-vehicles/${id}/sync-ops-compliance/`),
  getFleetMaintenanceAnalytics: (params = {}) =>
    api.get('/asset-inventory/fleet-vehicles/maintenance-analytics/', { params }),

  // Employee Self-Service (My Assets)
  getMyAssets: () => api.get('/asset-inventory/my-assets/'),
  getMyAssetsSummary: () => api.get('/asset-inventory/my-assets/summary/'),
  getPendingIncomingTransfers: () => api.get('/asset-inventory/my-assets/pending-incoming/'),
  getPendingOutgoingTransfers: () => api.get('/asset-inventory/my-assets/pending-outgoing/'),
  getPendingReceiptTransfers: () => api.get('/asset-inventory/my-assets/pending-receipt/'),
  getTransactionHistory: (page = 1, pageSize = 20) =>
    api.get('/asset-inventory/my-assets/transaction-history/', { params: { page, page_size: pageSize } }),
  initiateTransfer: (assetId, data) => api.post(`/asset-inventory/my-assets/${assetId}/initiate-transfer/`, data),
  
  // Employee Return to Inventory
  getPendingEmployeeReturns: () => api.get('/asset-inventory/my-assets/pending-returns/'),
  initiateReturn: (assetId, data) => api.post(`/asset-inventory/my-assets/${assetId}/initiate-return/`, data),
  cancelReturn: (transferId, data) => api.post(`/asset-inventory/my-assets/returns/${transferId}/cancel/`, data),

  // Transfer Actions
  acceptTransfer: (transferId, data = {}) => {
    if (data.photos && data.photos.length > 0) {
      const form = new FormData();
      form.append('condition', data.condition ?? '');
      data.photos.forEach((file) => form.append('photos', file));
      // CRITICAL: Don't set Content-Type manually - browser will set it with boundary
      return api.post(`/asset-inventory/transfer-actions/${transferId}/accept/`, form);
    }
    // Send JSON when no photos
    return api.post(`/asset-inventory/transfer-actions/${transferId}/accept/`, {
      condition: data.condition || ''
    });
  },
  acknowledgeReceipt: (transferId, data = {}) => {
    if (data.photos && data.photos.length > 0) {
      const form = new FormData();
      form.append('condition', data.condition ?? '');
      data.photos.forEach((file) => form.append('photos', file));
      // CRITICAL: Don't set Content-Type manually - browser will set it with boundary
      return api.post(`/asset-inventory/transfer-actions/${transferId}/acknowledge-receipt/`, form);
    }
    // Send JSON when no photos
    return api.post(`/asset-inventory/transfer-actions/${transferId}/acknowledge-receipt/`, {
      condition: data.condition || ''
    });
  },
  rejectTransfer: (transferId, data) => api.post(`/asset-inventory/transfer-actions/${transferId}/reject/`, data),
  cancelTransfer: (transferId, data = {}) => api.post(`/asset-inventory/transfer-actions/${transferId}/cancel/`, data),
  
  // Custodian Pending Returns
  getPendingReturnsForCustodian: () => api.get('/asset-inventory/pending-returns/'),
  confirmReturn: (transferId, data = {}) => {
    if (data.photos && data.photos.length > 0) {
      const form = new FormData();
      form.append('location', data.location ?? 'Inventory');
      form.append('condition', data.condition ?? '');
      data.photos.forEach((file) => form.append('photos', file));
      return api.post(`/asset-inventory/pending-returns/${transferId}/confirm/`, form, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
    }
    return api.post(`/asset-inventory/pending-returns/${transferId}/confirm/`, {
      location: data.location,
      condition: data.condition,
    });
  },
  rejectReturn: (transferId, data) => api.post(`/asset-inventory/pending-returns/${transferId}/reject/`, data),

  // Vendors
  getVendors: (params = {}) => api.get('/asset-inventory/vendors/', { params }),
  createVendor: (data) => api.post('/asset-inventory/vendors/', data),
  updateVendor: (id, data) => api.patch(`/asset-inventory/vendors/${id}/`, data),
  verifyVendor: (id) => api.post(`/asset-inventory/vendors/${id}/verify/`),
  verifyVendorUEN: (uen) => api.post('/asset-inventory/vendors/verify-uen/', { uen }),

  // Insurance providers (fleet motor insurance master data)
  getInsuranceProviders: (params = {}) => api.get('/asset-inventory/insurance-providers/', { params }),
  createInsuranceProvider: (data) => api.post('/asset-inventory/insurance-providers/', data),
  updateInsuranceProvider: (id, data) => api.patch(`/asset-inventory/insurance-providers/${id}/`, data),
  verifyInsuranceProvider: (id) => api.post(`/asset-inventory/insurance-providers/${id}/verify/`),
  verifyInsuranceProviderUEN: (uen) => api.post('/asset-inventory/insurance-providers/verify-uen/', { uen }),

  // Vehicle lessors (fleet lease / rental companies)
  getVehicleLessors: (params = {}) => api.get('/asset-inventory/vehicle-lessors/', { params }),
  createVehicleLessor: (data) => api.post('/asset-inventory/vehicle-lessors/', data),
  updateVehicleLessor: (id, data) => api.patch(`/asset-inventory/vehicle-lessors/${id}/`, data),
  verifyVehicleLessor: (id) => api.post(`/asset-inventory/vehicle-lessors/${id}/verify/`),
  verifyVehicleLessorUEN: (uen) => api.post('/asset-inventory/vehicle-lessors/verify-uen/', { uen }),

  // Workshops (external maintenance / repair garages)
  getWorkshops: (params = {}) => api.get('/asset-inventory/workshops/', { params }),
  createWorkshop: (data) => api.post('/asset-inventory/workshops/', data),
  updateWorkshop: (id, data) => api.patch(`/asset-inventory/workshops/${id}/`, data),

  // Service parts catalog (multi-select source for return-from-workshop)
  getServiceParts: (params = {}) => api.get('/asset-inventory/service-parts/', { params }),
  createServicePart: (data) => api.post('/asset-inventory/service-parts/', data),
  updateServicePart: (id, data) => api.patch(`/asset-inventory/service-parts/${id}/`, data),

  // Send a fleet vehicle to a workshop (multipart: signature + handover details)
  sendToWorkshop: (assetId, data = {}) => {
    const form = new FormData();
    form.append('workshop_id', data.workshopId);
    form.append('signer_name', data.signerName || '');
    form.append('signature_base64', data.signatureBase64 || '');
    form.append('notes', data.notes || '');
    form.append('condition', data.condition || '');
    return api.post(`/asset-inventory/my-assets/${assetId}/send-to-workshop/`, form);
  },

  // Return a vehicle from the workshop by uploading the service chit
  returnFromWorkshop: (assetId, data = {}) => {
    const form = new FormData();
    form.append('service_chit', data.serviceChit);
    (data.partsServiced || []).forEach((code) => form.append('parts_serviced', code));
    form.append('notes', data.notes || '');
    form.append('condition', data.condition || '');
    return api.post(`/asset-inventory/my-assets/${assetId}/return-from-workshop/`, form);
  },

  // Monthly vehicle inspection log — driver self-service (My Assets)
  getMyInspectionsDue: () => api.get('/asset-inventory/my-assets/inspections-due/'),
  getInspectionTemplate: () => api.get('/asset-inventory/my-assets/inspection-template/'),
  // Submit the driver's monthly checklist. No inspection date — the server
  // stamps today. `answers`/`remarks` are keyed by template item key;
  // `photos` is { itemKey: [File, ...] } for flagged items.
  submitVehicleInspection: (assetId, data = {}) => {
    const form = new FormData();
    form.append('notes', data.notes || '');
    Object.entries(data.answers || {}).forEach(([key, value]) => {
      form.append(`answer_${key}`, value ?? '');
    });
    Object.entries(data.remarks || {}).forEach(([key, value]) => {
      if (value) form.append(`remark_${key}`, value);
    });
    Object.entries(data.photos || {}).forEach(([key, files]) => {
      (files || []).forEach((file) => form.append(`photo_${key}`, file));
    });
    return api.post(`/asset-inventory/my-assets/${assetId}/submit-inspection/`, form);
  },

  // Monthly vehicle inspection log — custodian / admin dashboard
  getVehicleInspections: (params = {}) => api.get('/asset-inventory/vehicle-inspections/', { params }),
  getVehicleInspection: (id) => api.get(`/asset-inventory/vehicle-inspections/${id}/`),
  getInspectionsDueStatus: (params = {}) =>
    api.get('/asset-inventory/vehicle-inspections/due-status/', { params }),
  getInspectionExceptions: (params = {}) =>
    api.get('/asset-inventory/vehicle-inspections/exceptions/', { params }),
  // Clear or replace a bad historical odometer (custodian escape hatch).
  correctVehicleInspectionOdometer: (id, data = {}) =>
    api.post(`/asset-inventory/vehicle-inspections/${id}/correct-odometer/`, data),

  // ── Traffic offence notices — custodian (Ops-sourced) ────────────────────
  getTrafficOffences: (params = {}) => api.get('/asset-inventory/traffic-offences/', { params }),
  getTrafficOffence: (id) => api.get(`/asset-inventory/traffic-offences/${id}/`),
  getTrafficOffenceSummary: () => api.get('/asset-inventory/traffic-offences/summary/'),
  getTrafficOffenceSyncStatus: () =>
    api.get('/asset-inventory/traffic-offences/sync-status/'),
  syncTrafficOffencesFromOps: () =>
    api.post('/asset-inventory/traffic-offences/sync/'),
  setOffenceDeclarationDeadline: (id, declarationDeadline) =>
    api.post(`/asset-inventory/traffic-offences/${id}/set-declaration-deadline/`, {
      declaration_deadline: declarationDeadline,
    }),
  // Preview the custody match when (re)proposing a driver.
  suggestOffenceDriver: (params = {}) =>
    api.get('/asset-inventory/traffic-offences/suggest-driver/', { params }),
  proposeOffenceDriver: (id, data = {}) =>
    api.post(`/asset-inventory/traffic-offences/${id}/propose-driver/`, data),
  // NRIC/address for the offline declaration. Every call is PDPA-audited
  // server-side and only works once the driver has confirmed.
  getOffenceParticulars: (id) =>
    api.get(`/asset-inventory/traffic-offences/${id}/particulars/`),
  markOffenceSubmitted: (id, data = {}) =>
    api.post(`/asset-inventory/traffic-offences/${id}/mark-submitted/`, data),
  uploadOffencePayment: (id, file, paymentReference = '') => {
    const form = new FormData();
    form.append('payment_proof', file);
    if (paymentReference) form.append('payment_reference', paymentReference);
    return api.post(`/asset-inventory/traffic-offences/${id}/upload-payment/`, form);
  },
  verifyOffencePayment: (id, data = {}) =>
    api.post(`/asset-inventory/traffic-offences/${id}/verify-payment/`, data),
  cancelTrafficOffence: (id, reason) =>
    api.post(`/asset-inventory/traffic-offences/${id}/cancel/`, { reason }),

  // ── Traffic offence notices — driver self-service ────────────────────────
  getMyOffences: () => api.get('/asset-inventory/my-assets/my-offences/'),
  myOffenceDocumentUrl: (noticeId) =>
    `/asset-inventory/my-assets/offence-document/?notice_id=${noticeId}`,
  respondToOffence: (data = {}) =>
    api.post('/asset-inventory/my-assets/offence-respond/', data),
  uploadMyOffenceReceipt: (noticeId, file, paymentReference = '') => {
    const form = new FormData();
    form.append('notice_id', noticeId);
    form.append('payment_proof', file);
    if (paymentReference) form.append('payment_reference', paymentReference);
    return api.post('/asset-inventory/my-assets/offence-receipt/', form);
  },

  // ── Vehicle accidents — custodian ────────────────────────────────────────
  getVehicleAccidents: (params = {}) =>
    api.get('/asset-inventory/vehicle-accidents/', { params }),
  getVehicleAccident: (id) => api.get(`/asset-inventory/vehicle-accidents/${id}/`),
  getAccidentSummary: () => api.get('/asset-inventory/vehicle-accidents/summary/'),
  getAccidentVehicleStats: (params = {}) =>
    api.get('/asset-inventory/vehicle-accidents/vehicle-stats/', { params }),
  derivePoliceRequired: (data = {}) =>
    api.post('/asset-inventory/vehicle-accidents/derive-police-required/', data),
  createVehicleAccident: (data = {}) => {
    const form = new FormData();
    Object.entries(data).forEach(([key, value]) => {
      if (value !== null && value !== undefined && value !== '') form.append(key, value);
    });
    return api.post('/asset-inventory/vehicle-accidents/', form);
  },
  markAccidentInsurerNotified: (id, data = {}) =>
    api.post(`/asset-inventory/vehicle-accidents/${id}/mark-insurer-notified/`, data),
  setAccidentReportingBasis: (id, data = {}) =>
    api.post(`/asset-inventory/vehicle-accidents/${id}/set-reporting-basis/`, data),
  recordAccidentPoliceReport: (id, data = {}) =>
    api.post(`/asset-inventory/vehicle-accidents/${id}/record-police-report/`, data),
  uploadAccidentSas: (id, file) => {
    const form = new FormData();
    form.append('sas_document', file);
    return api.post(`/asset-inventory/vehicle-accidents/${id}/upload-sas/`, form);
  },
  addAccidentPhotos: (id, photos, { photoType = 'scene', caption = '' } = {}) => {
    const form = new FormData();
    (photos || []).forEach((file) => form.append('photos', file));
    if (photoType) form.append('photo_type', photoType);
    if (caption) form.append('caption', caption);
    return api.post(`/asset-inventory/vehicle-accidents/${id}/photos/`, form);
  },
  recordAccidentClaim: (id, data = {}) => {
    const form = new FormData();
    Object.entries(data).forEach(([key, value]) => {
      if (value !== null && value !== undefined && value !== '') form.append(key, value);
    });
    return api.post(`/asset-inventory/vehicle-accidents/${id}/record-claim/`, form);
  },
  linkAccidentRepair: (id, maintenanceLogId) =>
    api.post(`/asset-inventory/vehicle-accidents/${id}/link-repair/`, {
      maintenance_log_id: maintenanceLogId,
    }),
  changeAccidentDriver: (id, driverStaffId) =>
    api.post(`/asset-inventory/vehicle-accidents/${id}/change-driver/`, {
      driver_staff_id: driverStaffId,
    }),
  closeAccident: (id) => api.post(`/asset-inventory/vehicle-accidents/${id}/close/`),
  cancelAccident: (id, reason) =>
    api.post(`/asset-inventory/vehicle-accidents/${id}/cancel/`, { reason }),
  accidentSasDocumentUrl: (id) =>
    `/asset-inventory/vehicle-accidents/${id}/sas-document/`,
  accidentPhotoUrl: (id, photoId) =>
    `/asset-inventory/vehicle-accidents/${id}/photo/${photoId}/`,
  accidentPrivateSettlementUrl: (id) =>
    `/asset-inventory/vehicle-accidents/${id}/private-settlement-document/`,

  // ── Vehicle accidents — driver self-service ──────────────────────────────
  getMyAccidents: () => api.get('/asset-inventory/my-assets/my-accidents/'),
  reportMyAccident: (data = {}) => {
    const form = new FormData();
    Object.entries(data).forEach(([key, value]) => {
      if (value !== null && value !== undefined && value !== '') form.append(key, value);
    });
    return api.post('/asset-inventory/my-assets/report-accident/', form);
  },
  uploadMyAccidentPhotos: (accidentId, photos, { photoType = 'scene', caption = '' } = {}) => {
    const form = new FormData();
    form.append('accident_id', accidentId);
    (photos || []).forEach((file) => form.append('photos', file));
    if (photoType) form.append('photo_type', photoType);
    if (caption) form.append('caption', caption);
    return api.post('/asset-inventory/my-assets/accident-photos/', form);
  },
  uploadMyAccidentSas: (accidentId, file) => {
    const form = new FormData();
    form.append('accident_id', accidentId);
    form.append('sas_document', file);
    return api.post('/asset-inventory/my-assets/accident-sas/', form);
  },
  myAccidentSasUrl: (accidentId) =>
    `/asset-inventory/my-assets/accident-sas/?accident_id=${accidentId}`,

};

// External / outsourced asset holders (e.g. JB office). Not employees, no
// login — registered by asset custodians purely so assets can be assigned and
// acknowledged via emailed magic links. Backend: /external-personnel/.
export const externalPersonnelAPI = {
  list: (params = {}) => api.get('/external-personnel/', { params }),
  get: (staffId) => api.get(`/external-personnel/${staffId}/`),
  register: (data) => api.post('/external-personnel/', data),
  offboard: (staffId) => api.post(`/external-personnel/${staffId}/offboard/`),
  resendLink: (staffId) => api.post(`/external-personnel/${staffId}/resend-link/`),
  // Work sites (ProjectWorkSite) for the office dropdown. The JB office lives
  // under project P0000-018; callers may pass a project code to scope results.
  getWorkSites: (projectCode) =>
    api.get('/work-sites/', { params: projectCode ? { project: projectCode } : {} }),
};

// Digital Name Card API
export const nameCardAPI = {
  getMyNameCard: () => api.get('/staff/me/name-card/'),
  createNameCard: (data) => api.post('/staff/me/name-card/create/', data),
  updateNameCard: (data) => api.patch('/staff/me/name-card/update/', data),
  deleteNameCard: () => api.delete('/staff/me/name-card/delete/'),
  regenerateLink: () => api.post('/staff/me/name-card/regenerate/'),
  getQRCode: () => api.get('/staff/me/name-card/qr-code/', { responseType: 'blob' }),
};

// Combine all API methods into a single object for default export
const combinedAPI = {
  // Spread specific APIs that do not define generic HTTP verb names
  ...staffAPI,
  ...confidentialAPI,
  ...apiUtils,
  ...dormitoryAPI,
  ...assetInventoryAPI,

  // Core axios methods last (authoritative)
  get: api.get.bind(api),
  post: api.post.bind(api),
  put: api.put.bind(api),
  patch: api.patch.bind(api),
  delete: api.delete.bind(api),

  // Namespaced configuration helpers
  config: {
    ...configAPI,
  },
  
  // Asset Inventory namespace
  assets: {
    ...assetInventoryAPI,
  },
};

// ---------------------------------------------------------------------------
// RO Team Dashboard API
// ---------------------------------------------------------------------------
export const roTeamAPI = {
  /**
   * Today's (or a specific date's) team snapshot.
   * @param {string} [date] - Optional YYYY-MM-DD string. Defaults to today.
   */
  getOverview: (date) =>
    api.get('/ro-team/overview/', { params: date ? { date } : {} }),

  /**
   * Classification-aware weekly trends.
   * @param {number} [weeks=8] - Number of complete past weeks to return (1–52).
   */
  getTrends: (weeks) =>
    api.get('/ro-team/trends/', { params: weeks ? { weeks } : {} }),

  /**
   * Year-level leave utilisation and behavioural patterns.
   * @param {number} [year] - 4-digit year. Defaults to current year.
   */
  getLeaveInsights: (year) =>
    api.get('/ro-team/leave-insights/', { params: year ? { year } : {} }),

  /**
   * Rich per-employee month detail.
   * @param {string|number} staffId - Primary key of the direct report.
   * @param {string} [month] - Optional YYYY-MM string. Defaults to current month.
   */
  getIndividualDetail: (staffId, month) =>
    api.get('/ro-team/individual/', {
      params: { staff_id: staffId, ...(month ? { month } : {}) },
    }),

  /**
   * Approved leave starting within the next N days for team members.
   * @param {number} [days=7] - Lookahead window (1–30).
   */
  getUpcomingLeave: (days) =>
    api.get('/ro-team/upcoming-leave/', { params: days ? { days } : {} }),
};

// ---------------------------------------------------------------------------
// HR Admin Attendance Overview API
// ---------------------------------------------------------------------------
export const attendanceOverviewAPI = {
  /**
   * Org-wide action-item counts — no segment filter.
   * Workers approaching OT cap, high consecutive days, staff burnout signals,
   * stale exceptions, zero leave utilisation.
   */
  getAlerts: () => api.get('/attendance-overview/alerts/'),

  /**
   * Department heatmap, hours distribution, sparklines.
   * @param {string} segment  - 'staff' | 'worker'
   * @param {number} [weeks=8] - Trailing weeks to include (1–26).
   */
  getWorkloadHealth: (segment, weeks) =>
    api.get('/attendance-overview/workload-health/', {
      params: { segment, ...(weeks ? { weeks } : {}) },
    }),

  /**
   * Worker: OT compliance + rest-day compliance + exception backlog.
   * Staff:  Extended-hours overview + exception backlog.
   * @param {string} segment - 'staff' | 'worker'
   */
  getComplianceSummary: (segment) =>
    api.get('/attendance-overview/compliance-summary/', { params: { segment } }),

  /**
   * AL utilisation distribution, MC monthly trend, Mon/Fri leave pattern.
   * @param {string} segment   - 'staff' | 'worker'
   * @param {number} [months=6] - Trailing months for MC trend (1–12).
   */
  getLeaveHealth: (segment, months) =>
    api.get('/attendance-overview/leave-health/', {
      params: { segment, ...(months ? { months } : {}) },
    }),

  /**
   * Punctuality & lateness: clock-in vs scheduled start (net of grace).
   * @param {string} segment  - 'staff' | 'worker'
   * @param {number} [weeks=8] - Trailing weeks to include (1–26).
   */
  getPunctuality: (segment, weeks) =>
    api.get('/attendance-overview/punctuality/', {
      params: { segment, ...(weeks ? { weeks } : {}) },
    }),

  /**
   * Raw ClockEvent hours vs AttendanceRecord payroll hours — delta by department.
   * @param {string} segment - 'staff' | 'worker'
   */
  getReconciliation: (segment) =>
    api.get('/attendance-overview/reconciliation/', { params: { segment } }),

  /**
   * Per-employee attendance detail for admin Employee Lookup tab.
   * @param {number|string} staffId - Primary key of the employee.
   * @param {string} [month] - Optional YYYY-MM string (defaults to current month).
   */
  getIndividual: (staffId, month) =>
    api.get('/attendance-overview/individual/', {
      params: { staff_id: staffId, ...(month ? { month } : {}) },
    }),
};

// ---------------------------------------------------------------------------
// HOD Team Dashboard API
// ---------------------------------------------------------------------------
export const hodTeamAPI = {
  getOverview: (date) =>
    api.get('/hod-team/overview/', { params: date ? { date } : {} }),

  getTrends: (weeks) =>
    api.get('/hod-team/trends/', { params: weeks ? { weeks } : {} }),

  getLeaveInsights: (year) =>
    api.get('/hod-team/leave-insights/', { params: year ? { year } : {} }),

  getIndividualDetail: (staffId, month) =>
    api.get('/hod-team/individual/', {
      params: { staff_id: staffId, ...(month ? { month } : {}) },
    }),

  getRoBreakdown: () => api.get('/hod-team/ro-breakdown/'),

  getUpcomingLeave: (days) =>
    api.get('/hod-team/upcoming-leave/', { params: days ? { days } : {} }),
};

// ---------------------------------------------------------------------------
// Portal Group Management API (System Admin)
// ---------------------------------------------------------------------------
export const workerCostDigestAPI = {
  getAdmin: () => api.get('/worker-cost-digest/admin/'),
  updateAdmin: (payload) => api.patch('/worker-cost-digest/admin/', payload),
  sendNow: (force = false) => api.post(
    '/worker-cost-digest/admin/send/',
    { force },
    { timeout: 180000 },
  ),
  downloadAdmin: (deliveryId) => api.get(
    `/worker-cost-digest/admin/runs/${deliveryId}/download/`,
    { responseType: 'blob', timeout: 60000 },
  ),
  getMine: () => api.get('/worker-cost-digest/mine/'),
  downloadMine: (deliveryId) => api.get(
    `/worker-cost-digest/mine/${deliveryId}/download/`,
    { responseType: 'blob', timeout: 60000 },
  ),
};

export const portalGroupAPI = {
  getAdminUsers: () => api.get('/admin/admin-users/'),
  getGroups: () => api.get('/admin/portal-groups/'),
  getMembers: (groupName) => api.get(`/admin/portal-groups/${groupName}/members/`),
  addMember: (groupName, staffId) =>
    api.post(`/admin/portal-groups/${groupName}/add-member/`, { staff_id: staffId }),
  removeMember: (groupName, staffId) =>
    api.post(`/admin/portal-groups/${groupName}/remove-member/`, { staff_id: staffId }),
};

// ---------------------------------------------------------------------------
// Talent Assessments API — Big Five Profile (IPIP-NEO-120)
// ---------------------------------------------------------------------------
// Employee-facing endpoints operate on request.user only. Admin endpoints
// require IsHrAdmin and write to AssessmentAccessLog with a justification.
export const talentAssessmentsAPI = {
  // Status / intro
  getBigFiveStatus: () => api.get('/talent-assessments/big-five/status/'),

  // Consent
  getBigFiveConsentTemplate: (language = 'en') =>
    api.get('/talent-assessments/big-five/consent/', { params: { language } }),
  recordBigFiveConsent: (languageCode = 'en') =>
    api.post('/talent-assessments/big-five/consent/agree/', { language_code: languageCode }),
  withdrawBigFiveConsent: (reason = '') =>
    api.post('/talent-assessments/big-five/consent/withdraw/', { reason }),

  // Items + scale
  getBigFiveBundle: (language = 'en') =>
    api.get('/talent-assessments/big-five/items/', { params: { language } }),

  // Sessions
  startBigFiveSession: (payload = {}) =>
    api.post('/talent-assessments/big-five/sessions/', payload),
  getBigFiveSession: (sessionId) =>
    api.get(`/talent-assessments/big-five/sessions/${sessionId}/`),
  saveBigFiveResponse: (sessionId, payload) =>
    api.post(`/talent-assessments/big-five/sessions/${sessionId}/responses/`, payload),
  submitBigFiveSession: (sessionId) =>
    api.post(`/talent-assessments/big-five/sessions/${sessionId}/submit/`),
  voidBigFiveSession: (sessionId) =>
    api.post(`/talent-assessments/big-five/sessions/${sessionId}/void/`),

  // Result + access log
  getMyBigFiveResult: () => api.get('/talent-assessments/big-five/result/'),
  getMyBigFiveAccessLog: () =>
    api.get('/talent-assessments/big-five/access-log/'),

  // Admin (Talent Insights)
  adminGetBigFiveAggregate: (params = {}) =>
    api.get('/talent-assessments/admin/big-five/aggregate/', { params }),
  adminGetBigFiveDirectory: () =>
    api.get('/talent-assessments/admin/big-five/employees/'),
  adminGetBigFiveIndividualResult: (staffId, payload, config) =>
    api.post(`/talent-assessments/admin/big-five/employees/${staffId}/`, payload, config),
  adminGetBigFiveAccessLog: (params = {}) =>
    api.get('/talent-assessments/admin/big-five/access-log/', { params }),

  // Team (HOD / Deputy HOD / RO) — audited, scoped
  getTeamBigFiveDirectory: () =>
    api.get('/talent-assessments/team/big-five/members/'),
  getTeamBigFiveDepartmentAggregate: () =>
    api.get('/talent-assessments/team/big-five/department-aggregate/'),
  getTeamBigFiveMemberStatus: (staffId) =>
    api.get(`/talent-assessments/team/big-five/members/${staffId}/status/`),
  getTeamBigFiveIndividualResult: (staffId, payload, config) =>
    api.post(`/talent-assessments/team/big-five/members/${staffId}/`, payload, config),
};

// Anonymous Misconduct Reporting (Whistleblowing)
// ------------------------------------------------
// CRITICAL ANONYMITY GUARANTEE: this client MUST NOT attach the logged-in
// user's auth token or send cookies. The whistleblowing channel is anonymous —
// the reporter's portal identity must never reach the backend. We therefore use
// a bare axios instance with NO request interceptor (the shared `api` instance
// injects the Bearer token) and `withCredentials: false` so no session cookie
// is sent. Content-Type is intentionally left unset so the browser adds the
// correct multipart/form-data boundary for the FormData payload.
const whistleblowingClient = axios.create({
  baseURL: API_BASE_URL,
  timeout: 60000, // uploads + SMTP delivery happen synchronously server-side
  withCredentials: false,
  headers: { Accept: 'application/json' },
});

export const whistleblowingAPI = {
  // formData: a FormData instance built by the page.
  submit: (formData) =>
    whistleblowingClient.post('/whistleblowing/submissions/', formData),
};

// ---------------------------------------------------------------------------
// Service API Keys / API Operations console
// ---------------------------------------------------------------------------
// Central HR-admin console at /admin/external-tokens. Asset custodians use the
// parallel asset-inventory/service-api-keys endpoints via ServiceApiKeysTab's
// configurable `apiBase` (they do not call this namespaced client).
export const reportingAPI = {
  getMonthlyLeaveUsage: (params) => api.get('/reporting/monthly-leave-usage/', { params }),
  exportMonthlyLeaveUsage: (params) => api.get('/reporting/monthly-leave-usage/export/', {
    params,
    responseType: 'blob',
  }),
  getLabourMarketSurvey: (params) => api.get('/reporting/labour-market-survey/', { params }),
  saveLabourMarketSurvey: (data) => api.patch('/reporting/labour-market-survey/', data),
};

export const serviceApiKeysAPI = {
  list: () => api.get('/service-api-keys/'),
  listScopes: () => api.get('/service-api-keys/scopes/'),
  create: (data) => api.post('/service-api-keys/create/', data),
  update: (id, data) => api.patch(`/service-api-keys/${id}/`, data),
  revoke: (id) => api.delete(`/service-api-keys/${id}/`),
  rotate: (id, data = {}) => api.post(`/service-api-keys/${id}/rotate/`, data),
  getOverview: (params = {}) => api.get('/service-api-keys/overview/', { params }),
  getRequestLogs: (params = {}) => api.get('/service-api-keys/request-logs/', { params }),
  getAdminActivity: (params = {}) => api.get('/service-api-keys/admin-activity/', { params }),
};

/**
 * Public origin for curl / integration examples shown to admins.
 * Prefer the browser origin in production; fall back to the configured API host.
 */
export const getPublicApiOrigin = () => {
  if (typeof window !== 'undefined' && window.location?.origin) {
    return window.location.origin;
  }
  try {
    if (API_BASE_URL.startsWith('http')) {
      return new URL(API_BASE_URL).origin;
    }
  } catch {
    // ignore
  }
  return 'https://hr.wepl.site';
};

// ---------------------------------------------------------------------------
// Recruitment / ATS API
// ---------------------------------------------------------------------------
export const recruitmentAPI = {
  // Applications
  listApplications: (params) => api.get('/confidential/recruitment/applications/', { params }),
  getApplication: (id) => api.get(`/confidential/recruitment/applications/${id}/`),
  createApplication: (data) => api.post('/confidential/recruitment/applications/', data),
  updateApplication: (id, data) => api.patch(`/confidential/recruitment/applications/${id}/`, data),
  deleteApplication: (id) => api.delete(`/confidential/recruitment/applications/${id}/`),
  transitionState: (id, data) => api.post(`/confidential/recruitment/applications/${id}/transition_state/`, data),
  getStateHistory: (id) => api.get(`/confidential/recruitment/applications/${id}/state_history/`),
  archiveApplication: (id) => api.post(`/confidential/recruitment/applications/${id}/archive/`),
  importTracker: (file, dryRun = false) => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('dry_run', dryRun ? 'true' : 'false');
    return api.post('/recruitment/import-tracker/', formData, { timeout: 120000 });
  },
  
  // Jobs
  listJobs: (params) => api.get('/confidential/recruitment/jobs/', { params }),
  getJob: (id) => api.get(`/confidential/recruitment/jobs/${id}/`),
  createJob: (data) => api.post('/confidential/recruitment/jobs/', data),
  updateJob: (id, data) => api.patch(`/confidential/recruitment/jobs/${id}/`, data),
  deleteJob: (id) => api.delete(`/confidential/recruitment/jobs/${id}/`),
  
  // Candidates
  listCandidates: (params) => api.get('/confidential/recruitment/candidates/', { params }),
  getCandidate: (id) => api.get(`/confidential/recruitment/candidates/${id}/`),
  createCandidate: (data) => api.post('/confidential/recruitment/candidates/', data),
  updateCandidate: (id, data) => api.patch(`/confidential/recruitment/candidates/${id}/`, data),
  deleteCandidate: (id) => api.delete(`/confidential/recruitment/candidates/${id}/`),
  
  // Reviews
  listReviews: (params) => api.get('/confidential/recruitment/reviews/', { params }),
  getReview: (id) => api.get(`/confidential/recruitment/reviews/${id}/`),
  createReview: (data) => api.post('/confidential/recruitment/reviews/', data),
  updateReview: (id, data) => api.patch(`/confidential/recruitment/reviews/${id}/`, data),
  deleteReview: (id) => api.delete(`/confidential/recruitment/reviews/${id}/`),
  
  // Interview Slots
  listInterviewSlots: (params) => api.get('/confidential/recruitment/interview-slots/', { params }),
  getInterviewSlot: (id) => api.get(`/confidential/recruitment/interview-slots/${id}/`),
  
  // Offer Letters
  listOfferLetters: (params) => api.get('/confidential/recruitment/offer-letters/', { params }),
  getOfferLetter: (id) => api.get(`/confidential/recruitment/offer-letters/${id}/`),
};

export default combinedAPI;
export { api };